import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  PageContainer,
  ContentGrid,
  Card,
  CardTitle,
  CardContent,
  ActionBar,
  Button,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  StatusBadge,
  TabContainer,
  TabList,
  Tab,
  TabContent,
  fadeIn,
  SearchInput
} from '../styles/PageStyles';
import { faWalkieTalkie, faRedo, faPause } from '@fortawesome/free-solid-svg-icons';
import { Bot, Workflow, Network } from 'lucide-react';
import PageHeaderWithInfo from '../components/common/PageHeaderWithInfo';
import Modal from '../components/modals/Modal';
import { post } from '../utils/apiUtils';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { format, isSameDay } from 'date-fns';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import { getAgents } from '../services/agentService';
import { getPipelineRuns } from '../services/pipelineService';
import Pagination from '../components/common/Pagination';
import { usePagination } from '../hooks/usePagination';
import {
  getObservabilityCount,
  getPipelinesByPagination,
  getPipelinesRun,
  getEntityCount,
  getTotalTokens,
  getTotalTokensPercentChangeLast24h,
  getTotalRunsCount,
  getRunCountPercentChangeLast24h,
  getSuccessRate,
  getSuccessRatePercentChangeLast24h,
  getFailedRuns,
  getFailedRunsPercentChangeLast24h,
} from '../services/ObservabilityService';
import { useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import SystemTrendChart from '../components/common/SystemTrendChart';
import PerformanceCard from '../components/observability/PerformanceCard';



const colors = {
  bg: "#1a2236",
  wrapper: "#233A67",
  card: "#3A4A77",
  // label: "#8fa3c0",
  label: "#ffffff",
  value: "#ffffff",
  sub: "#8fa3c0",
  up: "#4ade80",
  down: "#f87171",
};


const CardsWrapper = styled.div`
  display: flex;
  gap: 12px;
  background: ${colors.wrapper};
  border-radius: 16px;
  padding: 16px;
  width: 100%;
`;

const CardLayout = styled.div`
  background: ${colors.card};
  border-radius: 12px;
  padding: 18px 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CardLabel = styled.span`
  font-size: 16px;
  color: ${colors.label};
  font-weight: 500;
  letter-spacing: 0.01em;
`;

const CardValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${colors.value};
  line-height: 1.1;
  margin: 2px 0;
`;

const CardSub = styled.span`
  font-size: 16px;
  color: ${colors.sub};
  margin-bottom: 2px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 16px;
  font-weight: 600;
  color: ${({ $trend }) => ($trend === "up" ? colors.up : colors.down)};

  svg {
    width: 10px;
    height: 10px;
  }
`;


//Insight component styles

const Wrapper = styled.div`
  display: flex;
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  width: 100%;
  min-height: 420px;
`;

const ChartWrapper = styled.div`
  position: relative;
  flex: 1;
  min-height: 260px;
`;

/* ── Left Panel ─────────────────────────────────── */
const LeftPanel = styled.div`
  width: 30%;
  min-width: 280px;
  border-right: 1px solid #e5e7eb;
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
`;

const HeaderIcon = styled.div`
  width: 34px;
  height: 34px;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeaderTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #111827;
`;

const InsightList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
`;

const InsightItem = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const InsightAvatar = styled.div`
  width: 36px;
  height: 36px;
  min-width: 36px;
  background: #f3f4f6;
  border-radius: 50%;
`;

const InsightText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const InsightTitle = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: #111827;
`;

const InsightSub = styled.p`
  font-size: 12px;
  color: #6b7280;
`;

const ViewAllBtn = styled.button`
  margin-top: 24px;
  align-self: center;
  padding: 9px 28px;
  border: 1px solid #d1d5db;
  border-radius: 20px;
  background: transparent;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  font-family: inherit;
  &:hover { background: #f9fafb; }
`;

/* ── Right Panel ────────────────────────────────── */
const RightPanel = styled.div`
  flex: 1;
  width: 70%;
  padding: 24px 24px 16px;
  display: flex;
  flex-direction: column;
`;

const TabRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 20px;
  padding-bottom: 0;
`;

const TabGroup = styled.div`
  display: flex;
  gap: 0;
`;

// const Tab = styled.button`
//   background: none;
//   border: none;
//   font-family: inherit;
//   font-size: 14px;
//   font-weight: ${({ $active }) => ($active ? "600" : "400")};
//   color: ${({ $active }) => ($active ? "#111827" : "#9ca3af")};
//   padding: 0 4px 10px;
//   margin-right: 24px;
//   cursor: pointer;
//   border-bottom: ${({ $active }) => ($active ? "2px solid #2196f3" : "2px solid transparent")};
//   margin-bottom: -1.5px;
//   transition: all 0.2s;
// `;

const RangeSelect = styled.select`
  padding: 7px 104px 7px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 13px;
  font-family: inherit;
  background: #ffffff url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path fill='%23888' d='M6 8L1 3h10z'/></svg>") no-repeat right 8px center;
  appearance: none;
  color: #374151;
  cursor: pointer;
  &:focus { outline: none; border-color: #2196f3; }
`;

const LegendRow = styled.div`
  display: flex;
  gap: 2rem;
  margin-bottom: 2rem;
`;

const LegendItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #6b7280;
`;

const LegendDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ color }) => color};
`;

// ── sub-components ───────────────────────────────────────────────
const ArrowUp = () => (
  <svg viewBox="0 0 10 10" fill="currentColor">
    <polygon points="5,1 9,9 1,9" />
  </svg>
);

const ArrowDown = () => (
  <svg viewBox="0 0 10 10" fill="currentColor">
    <polygon points="5,9 9,1 1,1" />
  </svg>
);


const MetricCard = ({ label, value, sub, change, trend }) => (
  <CardLayout>
    <CardLabel>{label}</CardLabel>
    <CardValue>{value}</CardValue>
    <CardSub>{sub}</CardSub>
    <Badge $trend={trend}>
      {trend === "up" ? <ArrowUp /> : <ArrowDown />}
      {change}
    </Badge>
  </CardLayout>
);

// Small spinner for inline/loaders in this page
const InlineSpinner = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #e5e7eb;
  border-top-color: #0066FF;
  border-radius: 50%;
  margin-right: 8px;
  animation: spin 0.8s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const PerformanceCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 1.5rem;

  @media (max-width: 1200px) {
    gap: 0.75rem;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

// const SystemTrendChart = () => {

//   ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

// // ── data ─────────────────────────────────────────────────────────
// const LABELS = ["00:00", "02:00", "04:00", "06:00", "12:00", "18:00", "24:00", "Now"];
// const TOTAL_RUNS  = [105, 165, 145, 205, 205, 230, 205, 260];
// const FAILED_RUNS = [45,  48,  78,  50,  85,  65,  100, 70];

// // ── styled components ────────────────────────────────────────────
// // const GlobalStyle = createGlobalStyle`
// //   *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
// //   body { background: #f4f5f7; font-family: 'Segoe UI', system-ui, sans-serif;
// //     display: flex; align-items: center; justify-content: center;
// //     min-height: 100vh; padding: 32px; }
// // `;

// // ── chart config ──────────────────────────────────────────────────
// function buildChartData(ctx) {

//   const blueGrad = ctx.createLinearGradient(0, 0, 0, 260);
//   blueGrad.addColorStop(0, "rgba(41,182,246,0.28)");
//   blueGrad.addColorStop(1, "rgba(41,182,246,0.02)");

//   return {
//     labels: LABELS,
//     datasets: [
//       {
//         label: "Total Runs",
//         data: TOTAL_RUNS,
//         borderColor: "#29b6f6",
//         backgroundColor: blueGrad,
//         fill: true,
//         tension: 0.4,
//         pointBackgroundColor: "#ffffff",
//         pointBorderColor: "#29b6f6",
//         pointBorderWidth: 2,
//         pointRadius: 5,
//         pointHoverRadius: 7,
//         borderWidth: 2,
//       },
//       {
//         label: "Failed Runs",
//         data: FAILED_RUNS,
//         borderColor: "#ef5350",
//         backgroundColor: "transparent",
//         fill: false,
//         tension: 0.4,
//         pointBackgroundColor: "#ffffff",
//         pointBorderColor: "#ef5350",
//         pointBorderWidth: 2,
//         pointRadius: 5,
//         pointHoverRadius: 7,
//         borderWidth: 2,
//       },
//     ],
//   };
// }

// const chartOptions = {
//   responsive: true,
//   maintainAspectRatio: false,
//   animation: {
//     duration: 1200,
//     easing: "easeInOutQuart",
//     // left-to-right clip animation via clip
//     onProgress(anim) {
//       const chart = anim.chart;
//       const { ctx, chartArea } = chart;
//       if (!chartArea) return;
//       const prog = anim.currentStep / anim.numSteps;
//       ctx.save();
//       ctx.beginPath();
//       ctx.rect(chartArea.left, chartArea.top, chartArea.width * prog, chartArea.bottom - chartArea.top);
//       ctx.restore();
//     },
//   },
//   interaction: { mode: "index", intersect: false },
//   plugins: {
//     legend: { display: false },
//     tooltip: {
//       backgroundColor: "rgba(255,255,255,0.97)",
//       titleColor: "#111827",
//       bodyColor: "#4b5563",
//       borderColor: "#e5e7eb",
//       borderWidth: 1,
//       padding: 10,
//       cornerRadius: 8,
//     },
//   },
//   scales: {
//     x: {
//       grid: { color: "rgba(0,0,0,0.06)", drawTicks: false },
//       border: { dash: [4, 4] },
//       ticks: { color: "#9ca3af", font: { size: 11 }, autoSkip: false, maxRotation: 0 },
//     },
//     y: {
//       min: 0,
//       max: 320,
//       ticks: {
//         stepSize: 50,
//         color: "#9ca3af",
//         font: { size: 11 },
//         callback: (v) => (v === 0 ? "0" : v),
//       },
//       grid: { color: "rgba(0,0,0,0.05)", drawTicks: false },
//       border: { display: false },
//     },
//   },
// };

// // ── left-to-right animation plugin ───────────────────────────────
// const leftToRightPlugin = {
//   id: "leftToRight",
//   beforeDraw(chart) {
//     const anim = chart._animator;
//     if (!anim || !anim._running) return;
//     const { ctx, chartArea } = chart;
//     if (!chartArea) return;
//     const t = chart.getDatasetMeta(0)._parsed?.length ? 1 : 0;
//     ctx.save();
//     ctx.beginPath();
//     ctx.rect(0, 0, chartArea.left + chartArea.width * t, chart.height);
//     ctx.clip();
//   },
//   afterDraw(chart) {
//     chart.ctx.restore();
//   },
// };


//   const chartRef = useRef(null);
//   const [chartData, setChartData] = useState(null);

//   useEffect(() => {
//     if (chartRef.current) {
//       const ctx = chartRef.current.ctx;
//       setChartData(buildChartData(ctx));
//     }
//   }, []);

//   return (
//     <ChartWrapper>
//       {chartData && (
//         <Line
//           ref={chartRef}
//           data={chartData}
//           options={chartOptions}
//           plugins={[leftToRightPlugin]}
//         />
//       )}
//     </ChartWrapper>
//   );
// }


// --- Static data copied from data/teams_observability_dashboard.html ---

const TEAMS_DATA = [
  // {"team_id":"claims_intake","team_name":"Claims Intake & Registration","pipeline_name":"CLAIMS INTAKE","planner_id":"planner_claims_intake","domain_context":"Claims Platform + IDP Layer","status":"Active","last_active":"2 min ago"},
  // {"team_id":"claims_triage","team_name":"Claims Triage & Routing","pipeline_name":"CLAIMS TRIAGE","planner_id":"planner_claims_triage","domain_context":"Claims Platform + FWA + Configurator","status":"Active","last_active":"5 min ago"},
  // {"team_id":"medical_codes_mapping","team_name":"Medical Coding & Mapping","pipeline_name":"MEDICAL CODES MAPPING","planner_id":"planner_medical_codes_mapping","domain_context":"Claims + IDP + Provider Management","status":"Active","last_active":"7 min ago"},
  // {"team_id":"claims_history","team_name":"Claims History & Insights","pipeline_name":"CLAIMS HISTORY","planner_id":"planner_claims_history","domain_context":"Claims Platform + PAS + FWA","status":"Active","last_active":"10 min ago"},
  // {"team_id":"adjudication","team_name":"Adjudication & Benefit Determination","pipeline_name":"ADJUDICATION","planner_id":"planner_adjudication","domain_context":"Claims + Configurator + Provider Tariffs + FWA","status":"Active","last_active":"3 min ago"},
  // {"team_id":"fwa_detection","team_name":"Fraud, Waste & Abuse Detection","pipeline_name":"FRAUD, WASTE & ABUSE DETECTION","planner_id":"planner_fwa_detection","domain_context":"FWA Module + Claims + Provider","status":"Active","last_active":"8 min ago"},
  // {"team_id":"copilot","team_name":"Co-Pilot Workbench","pipeline_name":"CO-PILOT","planner_id":"planner_copilot","domain_context":"Claims Workbench + Configurator","status":"Active","last_active":"1 min ago"},
  // {"team_id":"communication","team_name":"Member Communication & Notifications","pipeline_name":"COMMUNICATION","planner_id":"planner_communication","domain_context":"Communication","status":"Inactive","last_active":"1 day ago"},
  // {"team_id":"closure_payment","team_name":"Closure & Payment Operations","pipeline_name":"CLOSURE & PAYMENT","planner_id":"planner_closure_payment","domain_context":"Closure & Payment","status":"Active","last_active":"30 min ago"},
  // {"team_id":"claims_summary","team_name":"Claims Summary & Decision Support","pipeline_name":"CLAIMS SUMMARY","planner_id":"planner_claims_summary","domain_context":"Claims Summary","status":"Inactive","last_active":"2 days ago"}
];

const PLANNERS_DATA = [
  // {"planner_id":"planner_claims_intake","planner_name":"Claims Intake","team_id":"claims_intake","core_module_mapping":"Claims Platform + IDP Layer","status":"Active","last_active":"2 min ago","llm_model":"GPT-5"},
  // {"planner_id":"planner_claims_triage","planner_name":"Claims Triage","team_id":"claims_triage","core_module_mapping":"Claims Platform + FWA + Configurator","status":"Active","last_active":"5 min ago","llm_model":"Claude-3"},
  // {"planner_id":"planner_medical_codes_mapping","planner_name":"Medical Codes Mapping","team_id":"medical_codes_mapping","core_module_mapping":"Claims + IDP + Provider Management","status":"Active","last_active":"7 min ago","llm_model":"Gemini-3"},
  // {"planner_id":"planner_claims_history","planner_name":"Claims History","team_id":"claims_history","core_module_mapping":"Claims Platform + PAS + FWA","status":"Active","last_active":"10 min ago","llm_model":"GPT-5"},
  // {"planner_id":"planner_adjudication","planner_name":"Adjudication","team_id":"adjudication","core_module_mapping":"Claims + Configurator + Provider Tariffs + FWA","status":"Active","last_active":"3 min ago","llm_model":"Claude-3"},
  // {"planner_id":"planner_fwa_detection","planner_name":"FWA Detection","team_id":"fwa_detection","core_module_mapping":"FWA Module + Claims + Provider","status":"Active","last_active":"8 min ago","llm_model":"Gemini-3"},
  // {"planner_id":"planner_copilot","planner_name":"Co-Pilot","team_id":"copilot","core_module_mapping":"Claims Workbench + Configurator","status":"Active","last_active":"1 min ago","llm_model":"GPT-5"},
  // {"planner_id":"planner_communication","planner_name":"Communication","team_id":"communication","core_module_mapping":"Communication","status":"Inactive","last_active":"1 day ago","llm_model":"Claude-3"},
  // {"planner_id":"planner_closure_payment","planner_name":"Closure & Payment","team_id":"closure_payment","core_module_mapping":"Closure & Payment","status":"Active","last_active":"30 min ago","llm_model":"Gemini-3"},
  // {"planner_id":"planner_claims_summary","planner_name":"Claims Summary","team_id":"claims_summary","core_module_mapping":"Claims Summary","status":"Inactive","last_active":"2 days ago","llm_model":"GPT-5"}
];

const TEAM_RUNS_DATA = [
  // {"team_run_id":"tr-claims-intake-001","team_id":"claims_intake","planner_id":"planner_claims_intake","status":"success","started_at":"2026-04-09T02:00:00Z","finished_at":"2026-04-09T02:00:12Z","duration_ms":12000,"total_tokens":4200},
  // {"team_run_id":"tr-claims-intake-002","team_id":"claims_intake","planner_id":"planner_claims_intake","status":"failed","started_at":"2026-04-09T03:15:00Z","finished_at":"2026-04-09T03:15:05Z","duration_ms":5000,"total_tokens":2100},
  // {"team_run_id":"tr-claims-triage-001","team_id":"claims_triage","planner_id":"planner_claims_triage","status":"success","started_at":"2026-04-09T02:30:00Z","finished_at":"2026-04-09T02:30:18Z","duration_ms":18000,"total_tokens":5300},
  // {"team_run_id":"tr-claims-triage-002","team_id":"claims_triage","planner_id":"planner_claims_triage","status":"running","started_at":"2026-04-09T04:00:00Z","finished_at":null,"duration_ms":null,"total_tokens":3200},
  // {"team_run_id":"tr-medical-codes-001","team_id":"medical_codes_mapping","planner_id":"planner_medical_codes_mapping","status":"success","started_at":"2026-04-09T01:45:00Z","finished_at":"2026-04-09T01:45:25Z","duration_ms":25000,"total_tokens":6100},
  // {"team_run_id":"tr-claims-history-001","team_id":"claims_history","planner_id":"planner_claims_history","status":"success","started_at":"2026-04-09T01:20:00Z","finished_at":"2026-04-09T01:20:15Z","duration_ms":15000,"total_tokens":4800},
  // {"team_run_id":"tr-adjudication-001","team_id":"adjudication","planner_id":"planner_adjudication","status":"failed","started_at":"2026-04-09T03:00:00Z","finished_at":"2026-04-09T03:00:20Z","duration_ms":20000,"total_tokens":7000},
  // {"team_run_id":"tr-fwa-detection-001","team_id":"fwa_detection","planner_id":"planner_fwa_detection","status":"success","started_at":"2026-04-09T02:10:00Z","finished_at":"2026-04-09T02:10:22Z","duration_ms":22000,"total_tokens":6500},
  // {"team_run_id":"tr-copilot-001","team_id":"copilot","planner_id":"planner_copilot","status":"success","started_at":"2026-04-09T02:05:00Z","finished_at":"2026-04-09T02:05:08Z","duration_ms":8000,"total_tokens":3900},
  // {"team_run_id":"tr-communication-001","team_id":"communication","planner_id":"planner_communication","status":"failed","started_at":"2026-04-08T16:00:00Z","finished_at":"2026-04-08T16:00:05Z","duration_ms":5000,"total_tokens":1500},
  // {"team_run_id":"tr-closure-payment-001","team_id":"closure_payment","planner_id":"planner_closure_payment","status":"success","started_at":"2026-04-09T01:55:00Z","finished_at":"2026-04-09T01:55:12Z","duration_ms":12000,"total_tokens":4100},
  // {"team_run_id":"tr-claims-summary-001","team_id":"claims_summary","planner_id":"planner_claims_summary","status":"success","started_at":"2026-04-08T15:30:00Z","finished_at":"2026-04-08T15:30:20Z","duration_ms":20000,"total_tokens":5600}
];

const PLANNER_RUNS_DATA = [
  // {"planner_run_id":"pr-claims-intake-001","planner_id":"planner_claims_intake","team_run_id":"tr-claims-intake-001","status":"success","started_at":"2026-04-09T02:00:00Z","finished_at":"2026-04-09T02:00:10Z","duration_ms":10000,"total_tokens":3800,"llm_model":"GPT-5"},
  // {"planner_run_id":"pr-claims-intake-002","planner_id":"planner_claims_intake","team_run_id":"tr-claims-intake-002","status":"failed","started_at":"2026-04-09T03:15:00Z","finished_at":"2026-04-09T03:15:04Z","duration_ms":4000,"total_tokens":1800,"llm_model":"GPT-5"},
  // {"planner_run_id":"pr-claims-triage-001","planner_id":"planner_claims_triage","team_run_id":"tr-claims-triage-001","status":"success","started_at":"2026-04-09T02:30:00Z","finished_at":"2026-04-09T02:30:16Z","duration_ms":16000,"total_tokens":5000,"llm_model":"Claude-3"},
  // {"planner_run_id":"pr-claims-triage-002","planner_id":"planner_claims_triage","team_run_id":"tr-claims-triage-002","status":"running","started_at":"2026-04-09T04:00:00Z","finished_at":null,"duration_ms":null,"total_tokens":3200,"llm_model":"Claude-3"},
  // {"planner_run_id":"pr-medical-codes-001","planner_id":"planner_medical_codes_mapping","team_run_id":"tr-medical-codes-001","status":"success","started_at":"2026-04-09T01:45:00Z","finished_at":"2026-04-09T01:45:23Z","duration_ms":23000,"total_tokens":5900,"llm_model":"Gemini-3"},
  // {"planner_run_id":"pr-claims-history-001","planner_id":"planner_claims_history","team_run_id":"tr-claims-history-001","status":"success","started_at":"2026-04-09T01:20:00Z","finished_at":"2026-04-09T01:20:13Z","duration_ms":13000,"total_tokens":4500,"llm_model":"GPT-5"},
  // {"planner_run_id":"pr-adjudication-001","planner_id":"planner_adjudication","team_run_id":"tr-adjudication-001","status":"failed","started_at":"2026-04-09T03:00:00Z","finished_at":"2026-04-09T03:00:18Z","duration_ms":18000,"total_tokens":6800,"llm_model":"Claude-3"},
  // {"planner_run_id":"pr-fwa-detection-001","planner_id":"planner_fwa_detection","team_run_id":"tr-fwa-detection-001","status":"success","started_at":"2026-04-09T02:10:00Z","finished_at":"2026-04-09T02:10:20Z","duration_ms":20000,"total_tokens":6200,"llm_model":"Gemini-3"},
  // {"planner_run_id":"pr-copilot-001","planner_id":"planner_copilot","team_run_id":"tr-copilot-001","status":"success","started_at":"2026-04-09T02:05:00Z","finished_at":"2026-04-09T02:05:07Z","duration_ms":7000,"total_tokens":3600,"llm_model":"GPT-5"},
  // {"planner_run_id":"pr-communication-001","planner_id":"planner_communication","team_run_id":"tr-communication-001","status":"failed","started_at":"2026-04-08T16:00:00Z","finished_at":"2026-04-08T16:00:04Z","duration_ms":4000,"total_tokens":1400,"llm_model":"Claude-3"},
  // {"planner_run_id":"pr-closure-payment-001","planner_id":"planner_closure_payment","team_run_id":"tr-closure-payment-001","status":"success","started_at":"2026-04-09T01:55:00Z","finished_at":"2026-04-09T01:55:10Z","duration_ms":10000,"total_tokens":3800,"llm_model":"Gemini-3"},
  // {"planner_run_id":"pr-claims-summary-001","planner_id":"planner_claims_summary","team_run_id":"tr-claims-summary-001","status":"success","started_at":"2026-04-08T15:30:00Z","finished_at":"2026-04-08T15:30:18Z","duration_ms":18000,"total_tokens":5300,"llm_model":"GPT-5"}
];

// Sample planner tool-call trace data based on data/planner_output.json.
// Shared across all planner runs for this static demo so every Planner Run
// details modal shows a Tool Calls table.
const SAMPLE_PLANNER_TOOL_CALLS = [
  // {
  //   tool_call_id: 'call_BQwLIH1Gh5nv81MQe4GOKQCa',
  //   tool_name: 'delegate_task_to_member',
  //   tool_args: {
  //     member_id: 'policy-and-rules-insight-agent',
  //     task:
  //       'Run Policy Rules Insight Agent for case MBMAACP-106_TC003 after retrieving the full Claim Snapshot along with structured policy and claim data.'
  //   },
  //   tool_call_error: false,
  //   result_summary:
  //     'Needs full structured claim snapshot and details for MBMAACP-106_TC003 before rules evaluation.',
  //   metrics: {},
  //   child_run_id: null,
  //   stop_after_tool_call: false,
  //   created_at: 1775714719,
  //   requires_confirmation: null,
  //   requires_user_input: null
  // },
  // {
  //   tool_call_id: 'call_R0EHrxeFNklDwADPAKY5dhea',
  //   tool_name: 'delegate_task_to_member',
  //   tool_args: {
  //     member_id: 'fwa-insights-agent',
  //     task:
  //       'Run FWA Insight analysis for case MBMAACP-106_TC003 using the Claim Snapshot along with data retrieved from knowledge base and outputs from the Policy Rules Insight Agent.'
  //   },
  //   tool_call_error: false,
  //   result_summary: 'Generated FWA risk summary and rule insights (data pending in sample).',
  //   metrics: {},
  //   child_run_id: null,
  //   stop_after_tool_call: false,
  //   created_at: 1775714725,
  //   requires_confirmation: null,
  //   requires_user_input: null
  // }
];

const PLANNER_TOOL_CALLS = {
  // 'pr-claims-intake-001': SAMPLE_PLANNER_TOOL_CALLS,
  // 'pr-claims-intake-002': SAMPLE_PLANNER_TOOL_CALLS,
  // 'pr-claims-triage-001': SAMPLE_PLANNER_TOOL_CALLS,
  // 'pr-claims-triage-002': SAMPLE_PLANNER_TOOL_CALLS,
  // 'pr-medical-codes-001': SAMPLE_PLANNER_TOOL_CALLS,
  // 'pr-claims-history-001': SAMPLE_PLANNER_TOOL_CALLS,
  // 'pr-adjudication-001': SAMPLE_PLANNER_TOOL_CALLS,
  // 'pr-fwa-detection-001': SAMPLE_PLANNER_TOOL_CALLS,
  // 'pr-copilot-001': SAMPLE_PLANNER_TOOL_CALLS,
  // 'pr-communication-001': SAMPLE_PLANNER_TOOL_CALLS,
  // 'pr-closure-payment-001': SAMPLE_PLANNER_TOOL_CALLS,
  // 'pr-claims-summary-001': SAMPLE_PLANNER_TOOL_CALLS
};

const SAMPLE_AGENT_TOOL_CALLS = [
  // {
  //   tool_call_id: 'agent_call_1',
  //   tool_name: 'Fetch product details',
  //   tool_args: {
  //     task: 'Fetch product details for product 12345 from catalog and pricing service.'
  //   },
  //   tool_call_error: false,
  //   child_run_id: null,
  //   stop_after_tool_call: false,
  //   created_at: 1775714800,
  //   requires_confirmation: null,
  //   requires_user_input: null
  // },
  // {
  //   tool_call_id: 'agent_call_2',
  //   tool_name: 'Send notification',
  //   tool_args: {
  //     task: 'Send claim status notification to customer via preferred channel.'
  //   },
  //   tool_call_error: false,
  //   child_run_id: null,
  //   stop_after_tool_call: false,
  //   created_at: 1775714810,
  //   requires_confirmation: null,
  //   requires_user_input: null
  // }
];

const AGENT_TOOL_CALLS = {
  // 'as-claims-intake-001': SAMPLE_AGENT_TOOL_CALLS,
  // 'as-claims-intake-002': SAMPLE_AGENT_TOOL_CALLS,
  // 'as-claims-intake-003': SAMPLE_AGENT_TOOL_CALLS,
  // 'as-claims-triage-001': SAMPLE_AGENT_TOOL_CALLS,
  // 'as-claims-triage-002': SAMPLE_AGENT_TOOL_CALLS,
  // 'as-medical-codes-001': SAMPLE_AGENT_TOOL_CALLS,
  // 'as-claims-history-001': SAMPLE_AGENT_TOOL_CALLS,
  // 'as-adjudication-001': SAMPLE_AGENT_TOOL_CALLS,
  // 'as-fwa-001': SAMPLE_AGENT_TOOL_CALLS,
  // 'as-copilot-001': SAMPLE_AGENT_TOOL_CALLS,
  // 'as-communication-001': SAMPLE_AGENT_TOOL_CALLS,
  // 'as-closure-001': SAMPLE_AGENT_TOOL_CALLS
};

const AGENT_SESSIONS_DATA = [
  // {"agent_session_id":"as-claims-intake-001","agent_id":"claims_intake_fnol_capture","planner_id":"planner_claims_intake","team_id":"claims_intake","status":"success","started_at":"2026-04-09T02:00:02Z","finished_at":"2026-04-09T02:00:06Z","duration_ms":4000,"total_tokens":950,"llm_model":"GPT-5","tool_call_count":2},
  // {"agent_session_id":"as-claims-intake-002","agent_id":"claims_intake_policy_validation","planner_id":"planner_claims_intake","team_id":"claims_intake","status":"success","started_at":"2026-04-09T02:00:06Z","finished_at":"2026-04-09T02:00:09Z","duration_ms":3000,"total_tokens":880,"llm_model":"GPT-5","tool_call_count":1},
  // {"agent_session_id":"as-claims-intake-003","agent_id":"claims_intake_document_extraction","planner_id":"planner_claims_intake","team_id":"claims_intake","status":"success","started_at":"2026-04-09T02:00:09Z","finished_at":"2026-04-09T02:00:12Z","duration_ms":3000,"total_tokens":1020,"llm_model":"GPT-5","tool_call_count":3},
  // {"agent_session_id":"as-claims-triage-001","agent_id":"claims_triage_complexity_classifier","planner_id":"planner_claims_triage","team_id":"claims_triage","status":"success","started_at":"2026-04-09T02:30:02Z","finished_at":"2026-04-09T02:30:08Z","duration_ms":6000,"total_tokens":1300,"llm_model":"Claude-3","tool_call_count":2},
  // {"agent_session_id":"as-claims-triage-002","agent_id":"claims_triage_fraud_pre_screener","planner_id":"planner_claims_triage","team_id":"claims_triage","status":"failed","started_at":"2026-04-09T02:30:08Z","finished_at":"2026-04-09T02:30:16Z","duration_ms":8000,"total_tokens":1500,"llm_model":"Claude-3","tool_call_count":3},
  // {"agent_session_id":"as-medical-codes-001","agent_id":"medical_codes_discharge_parser","planner_id":"planner_medical_codes_mapping","team_id":"medical_codes_mapping","status":"success","started_at":"2026-04-09T01:45:02Z","finished_at":"2026-04-09T01:45:10Z","duration_ms":8000,"total_tokens":1750,"llm_model":"Gemini-3","tool_call_count":2},
  // {"agent_session_id":"as-claims-history-001","agent_id":"claims_history_retrieval","planner_id":"planner_claims_history","team_id":"claims_history","status":"success","started_at":"2026-04-09T01:20:02Z","finished_at":"2026-04-09T01:20:09Z","duration_ms":7000,"total_tokens":1420,"llm_model":"GPT-5","tool_call_count":1},
  // {"agent_session_id":"as-adjudication-001","agent_id":"adjudication_eligibility_check","planner_id":"planner_adjudication","team_id":"adjudication","status":"failed","started_at":"2026-04-09T03:00:02Z","finished_at":"2026-04-09T03:00:10Z","duration_ms":8000,"total_tokens":2100,"llm_model":"Claude-3","tool_call_count":2},
  // {"agent_session_id":"as-fwa-001","agent_id":"fwa_detection_ml_scoring","planner_id":"planner_fwa_detection","team_id":"fwa_detection","status":"success","started_at":"2026-04-09T02:10:02Z","finished_at":"2026-04-09T02:10:15Z","duration_ms":13000,"total_tokens":1980,"llm_model":"Gemini-3","tool_call_count":3},
  // {"agent_session_id":"as-copilot-001","agent_id":"copilot_case_compilation","planner_id":"planner_copilot","team_id":"copilot","status":"success","started_at":"2026-04-09T02:05:02Z","finished_at":"2026-04-09T02:05:06Z","duration_ms":4000,"total_tokens":1100,"llm_model":"GPT-5","tool_call_count":1},
  // {"agent_session_id":"as-communication-001","agent_id":"communication_notification_trigger","planner_id":"planner_communication","team_id":"communication","status":"failed","started_at":"2026-04-08T16:00:02Z","finished_at":"2026-04-08T16:00:06Z","duration_ms":4000,"total_tokens":900,"llm_model":"Claude-3","tool_call_count":1},
  // {"agent_session_id":"as-closure-001","agent_id":"closure_payment_trigger","planner_id":"planner_closure_payment","team_id":"closure_payment","status":"success","started_at":"2026-04-09T01:55:02Z","finished_at":"2026-04-09T01:55:07Z","duration_ms":5000,"total_tokens":1200,"llm_model":"Gemini-3","tool_call_count":1}
];

const SYSTEM_HEALTH_DATA = [
  // {"id":"SYS-001","service":"API Gateway","cpu_pct":32,"memory_pct":45,"latency_ms":45,"throughput_rpm":1250,"error_rate_pct":0.02,"uptime_pct":99.99,"status":"Healthy"},
  // {"id":"SYS-002","service":"Agent Service","cpu_pct":48,"memory_pct":60,"latency_ms":120,"throughput_rpm":850,"error_rate_pct":0.05,"uptime_pct":99.98,"status":"Healthy"},
  // {"id":"SYS-003","service":"Knowledge Base","cpu_pct":25,"memory_pct":38,"latency_ms":65,"throughput_rpm":950,"error_rate_pct":0.01,"uptime_pct":99.99,"status":"Healthy"},
  // {"id":"SYS-004","service":"Document Processor","cpu_pct":78,"memory_pct":82,"latency_ms":210,"throughput_rpm":650,"error_rate_pct":0.8,"uptime_pct":99.95,"status":"Warning"},
  // {"id":"SYS-005","service":"Model Service","cpu_pct":55,"memory_pct":65,"latency_ms":180,"throughput_rpm":720,"error_rate_pct":0.1,"uptime_pct":99.97,"status":"Healthy"}
];

const SYSTEM_LOAD_DATA = [
  // {"label":"-24h","load_pct":30},
  // {"label":"-20h","load_pct":35},
  // {"label":"-16h","load_pct":40},
  // {"label":"-12h","load_pct":55},
  // {"label":"-8h","load_pct":70},
  // {"label":"-6h","load_pct":75},
  // {"label":"-4h","load_pct":65},
  // {"label":"-2h","load_pct":50},
  // {"label":"-1h","load_pct":45},
  // {"label":"-30m","load_pct":60},
  // {"label":"-15m","load_pct":68},
  // {"label":"Now","load_pct":58}
];

const SYSTEM_LOAD_BY_SERVICE = SYSTEM_LOAD_DATA.map((point, timeIndex) => {
  const base = { label: point.label };
  // const steps = Math.max(1, SYSTEM_LOAD_DATA.length - 1);
  // const tNorm = timeIndex / steps; // 0..1 across the 24h window

  // SYSTEM_HEALTH_DATA.forEach((svc, svcIndex) => {
  //   const midSvc = (SYSTEM_HEALTH_DATA.length - 1) / 2;

  //   let value = point.load_pct;

  //   const phase = svcIndex * 0.9;
  //   const wave = Math.sin(tNorm * Math.PI + phase); // -1..1

  //   const amplitude = 8; // +/- 8%
  //   value += wave * amplitude;

  //   const staticBias = (svcIndex - midSvc) * 1.5;
  //   value += staticBias;

  //   value = Math.max(0, Math.min(100, Math.round(value)));

  //   base[svc.id] = value;
  // });

  return base;
});

const SERVICE_COLORS = ['var(--primary)', '#34C759', '#FFCC00', '#FF3B30', '#A855F7'];

const ALERTS_DATA = [
  // {"id":"ALT-001","timestamp":"2025-03-17T15:45:22Z","service":"Document Processor","message":"High CPU utilization (78%)","severity":"Warning","status":"Active"},
  // {"id":"ALT-002","timestamp":"2025-03-17T15:30:18Z","service":"Document Analyzer Agent","message":"Response time degradation detected","severity":"Warning","status":"Active"},
  // {"id":"ALT-003","timestamp":"2025-03-17T14:55:10Z","service":"API Gateway","message":"Throughput spike detected (2000 req/min)","severity":"Info","status":"Resolved"},
  // {"id":"ALT-004","timestamp":"2025-03-17T12:55:30Z","service":"Knowledge Base","message":"Connection timeout to document store","severity":"Critical","status":"Resolved"},
  // {"id":"ALT-005","timestamp":"2025-03-17T10:40:12Z","service":"Model Service","message":"Increased error rate (0.5%)","severity":"Warning","status":"Resolved"}
];

const AGENTS_DATA = [
  // {"agent_id":"claims_intake_fnol_capture","agent_name":"FNOL Capture Agent","planner_id":"planner_claims_intake","team_id":"claims_intake","core_module_mapping":"Claims Platform + IDP Layer","shared_agent":false,"role":"Captures first notice of loss","status":"Active","last_active":"2 min ago","llm_model":"GPT-5"},
  // {"agent_id":"claims_intake_policy_validation","agent_name":"Policy Validation Agent","planner_id":"planner_claims_intake","team_id":"claims_intake","core_module_mapping":"Claims Platform + IDP Layer","shared_agent":false,"role":"Validates policy","status":"Active","last_active":"3 min ago","llm_model":"GPT-5"},
  // {"agent_id":"claims_intake_document_extraction","agent_name":"Document Extraction Agent","planner_id":"planner_claims_intake","team_id":"claims_intake","core_module_mapping":"Claims Platform + IDP Layer","shared_agent":true,"role":"Routes documents","status":"Active","last_active":"5 min ago","llm_model":"GPT-5"},
  // {"agent_id":"claims_intake_ocr_idp","agent_name":"OCR & IDP Agent","planner_id":"planner_claims_intake","team_id":"claims_intake","core_module_mapping":"Claims Platform + IDP Layer","shared_agent":true,"role":"Extracts data","status":"Active","last_active":"7 min ago","llm_model":"GPT-5"},
  // {"agent_id":"claims_intake_orchestrator","agent_name":"Intake Orchestrator Agent","planner_id":"planner_claims_intake","team_id":"claims_intake","core_module_mapping":"Claims Platform + IDP Layer","shared_agent":false,"role":"Coordinates flow","status":"Active","last_active":"2 min ago","llm_model":"GPT-5"},
  // {"agent_id":"claims_triage_complexity_classifier","agent_name":"Complexity Classifier Agent","planner_id":"planner_claims_triage","team_id":"claims_triage","core_module_mapping":"Claims Platform + FWA + Configurator","shared_agent":false,"role":"Classifies complexity","status":"Active","last_active":"5 min ago","llm_model":"Claude-3"},
  // {"agent_id":"claims_triage_urgency_scorer","agent_name":"Urgency Scorer Agent","planner_id":"planner_claims_triage","team_id":"claims_triage","core_module_mapping":"Claims Platform + FWA + Configurator","shared_agent":false,"role":"Assigns urgency","status":"Active","last_active":"6 min ago","llm_model":"Claude-3"},
  // {"agent_id":"claims_triage_fraud_pre_screener","agent_name":"Fraud Risk Pre-Screener Agent","planner_id":"planner_claims_triage","team_id":"claims_triage","core_module_mapping":"Claims Platform + FWA + Configurator","shared_agent":true,"role":"Flags risk","status":"Active","last_active":"8 min ago","llm_model":"Claude-3"},
  // {"agent_id":"claims_triage_routing_rules","agent_name":"Routing Rules Agent","planner_id":"planner_claims_triage","team_id":"claims_triage","core_module_mapping":"Claims Platform + FWA + Configurator","shared_agent":false,"role":"Routes claims","status":"Active","last_active":"9 min ago","llm_model":"Claude-3"},
  // {"agent_id":"claims_triage_orchestrator","agent_name":"Triage Orchestrator Agent","planner_id":"planner_claims_triage","team_id":"claims_triage","core_module_mapping":"Claims Platform + FWA + Configurator","shared_agent":false,"role":"Coordinates triage","status":"Active","last_active":"5 min ago","llm_model":"Claude-3"},
  // {"agent_id":"medical_codes_discharge_parser","agent_name":"Discharge Summary Parser Agent","planner_id":"planner_medical_codes_mapping","team_id":"medical_codes_mapping","core_module_mapping":"Claims + IDP + Provider Management","shared_agent":false,"role":"Extracts clinical data","status":"Active","last_active":"7 min ago","llm_model":"Gemini-3"},
  // {"agent_id":"medical_codes_icd_pcs_mapper","agent_name":"ICD / PCS Mapper Agent","planner_id":"planner_medical_codes_mapping","team_id":"medical_codes_mapping","core_module_mapping":"Claims + IDP + Provider Management","shared_agent":false,"role":"Maps diagnosis","status":"Active","last_active":"8 min ago","llm_model":"Gemini-3"},
  // {"agent_id":"medical_codes_procedure_mapper","agent_name":"CPT / TOSP / ACHI Mapper Agent","planner_id":"planner_medical_codes_mapping","team_id":"medical_codes_mapping","core_module_mapping":"Claims + IDP + Provider Management","shared_agent":false,"role":"Maps procedures","status":"Active","last_active":"9 min ago","llm_model":"Gemini-3"},
  // {"agent_id":"medical_codes_validation","agent_name":"Code Validation Agent","planner_id":"planner_medical_codes_mapping","team_id":"medical_codes_mapping","core_module_mapping":"Claims + IDP + Provider Management","shared_agent":false,"role":"Validates codes","status":"Active","last_active":"10 min ago","llm_model":"Gemini-3"},
  // {"agent_id":"medical_codes_reconciliation","agent_name":"Codes Reconciliation Agent","planner_id":"planner_medical_codes_mapping","team_id":"medical_codes_mapping","core_module_mapping":"Claims + IDP + Provider Management","shared_agent":false,"role":"Reconciles","status":"Active","last_active":"11 min ago","llm_model":"Gemini-3"},
  // {"agent_id":"claims_history_retrieval","agent_name":"Historical Claims Retrieval Agent","planner_id":"planner_claims_history","team_id":"claims_history","core_module_mapping":"Claims Platform + PAS + FWA","shared_agent":false,"role":"Retrieves history","status":"Active","last_active":"10 min ago","llm_model":"GPT-5"},
  // {"agent_id":"claims_history_policy_xref","agent_name":"Policy Cross-Reference Agent","planner_id":"planner_claims_history","team_id":"claims_history","core_module_mapping":"Claims Platform + PAS + FWA","shared_agent":false,"role":"Cross-reference","status":"Active","last_active":"12 min ago","llm_model":"GPT-5"},
  // {"agent_id":"claims_history_pattern_analysis","agent_name":"Claimant Pattern Analysis Agent","planner_id":"planner_claims_history","team_id":"claims_history","core_module_mapping":"Claims Platform + PAS + FWA","shared_agent":false,"role":"Pattern analysis","status":"Active","last_active":"15 min ago","llm_model":"GPT-5"},
  // {"agent_id":"claims_history_rsm_score","agent_name":"RSM Score Computation Agent","planner_id":"planner_claims_history","team_id":"claims_history","core_module_mapping":"Claims Platform + PAS + FWA","shared_agent":true,"role":"Risk score","status":"Active","last_active":"9 min ago","llm_model":"GPT-5"},
  // {"agent_id":"adjudication_eligibility_check","agent_name":"Eligibility Check Agent","planner_id":"planner_adjudication","team_id":"adjudication","core_module_mapping":"Claims + Configurator + Provider Tariffs + FWA","shared_agent":false,"role":"Eligibility","status":"Active","last_active":"3 min ago","llm_model":"Claude-3"},
  // {"agent_id":"adjudication_rules_engine","agent_name":"Benefit Rules Engine Agent","planner_id":"planner_adjudication","team_id":"adjudication","core_module_mapping":"Claims + Configurator + Provider Tariffs + FWA","shared_agent":false,"role":"Rules","status":"Active","last_active":"4 min ago","llm_model":"Claude-3"},
  // {"agent_id":"adjudication_exclusions","agent_name":"Exclusion & Waiting Period Agent","planner_id":"planner_adjudication","team_id":"adjudication","core_module_mapping":"Claims + Configurator + Provider Tariffs + FWA","shared_agent":false,"role":"Exclusions","status":"Active","last_active":"6 min ago","llm_model":"Claude-3"},
  // {"agent_id":"adjudication_tariff_payable","agent_name":"Tariff & Payable Amount Agent","planner_id":"planner_adjudication","team_id":"adjudication","core_module_mapping":"Claims + Configurator + Provider Tariffs + FWA","shared_agent":false,"role":"Payable","status":"Active","last_active":"7 min ago","llm_model":"Claude-3"},
  // {"agent_id":"adjudication_stp_decision","agent_name":"STP Decision Agent","planner_id":"planner_adjudication","team_id":"adjudication","core_module_mapping":"Claims + Configurator + Provider Tariffs + FWA","shared_agent":false,"role":"Decision","status":"Active","last_active":"8 min ago","llm_model":"Claude-3"},
  // {"agent_id":"adjudication_orchestrator","agent_name":"Adjudication Orchestrator Agent","planner_id":"planner_adjudication","team_id":"adjudication","core_module_mapping":"Claims + Configurator + Provider Tariffs + FWA","shared_agent":false,"role":"Orchestration","status":"Active","last_active":"3 min ago","llm_model":"Claude-3"},
  // {"agent_id":"fwa_detection_ml_scoring","agent_name":"ML Fraud Scoring Agent","planner_id":"planner_fwa_detection","team_id":"fwa_detection","core_module_mapping":"FWA Module + Claims + Provider","shared_agent":true,"role":"Fraud score","status":"Active","last_active":"8 min ago","llm_model":"Gemini-3"},
  // {"agent_id":"fwa_detection_rules_anomaly","agent_name":"Rules-Based Anomaly Agent","planner_id":"planner_fwa_detection","team_id":"fwa_detection","core_module_mapping":"FWA Module + Claims + Provider","shared_agent":false,"role":"Rules","status":"Active","last_active":"9 min ago","llm_model":"Gemini-3"},
  // {"agent_id":"fwa_detection_provider_behaviour","agent_name":"Provider Behaviour Analysis Agent","planner_id":"planner_fwa_detection","team_id":"fwa_detection","core_module_mapping":"FWA Module + Claims + Provider","shared_agent":false,"role":"Provider analysis","status":"Active","last_active":"10 min ago","llm_model":"Gemini-3"},
  // {"agent_id":"fwa_detection_leakage","agent_name":"Leakage Detection Agent","planner_id":"planner_fwa_detection","team_id":"fwa_detection","core_module_mapping":"FWA Module + Claims + Provider","shared_agent":false,"role":"Leakage","status":"Active","last_active":"11 min ago","llm_model":"Gemini-3"},
  // {"agent_id":"fwa_detection_investigation_flag","agent_name":"Investigation Flag Agent","planner_id":"planner_fwa_detection","team_id":"fwa_detection","core_module_mapping":"FWA Module + Claims + Provider","shared_agent":false,"role":"Investigation","status":"Active","last_active":"12 min ago","llm_model":"Gemini-3"},
  // {"agent_id":"copilot_case_compilation","agent_name":"Case Compilation Agent","planner_id":"planner_copilot","team_id":"copilot","core_module_mapping":"Claims Workbench + Configurator","shared_agent":false,"role":"Compile","status":"Active","last_active":"1 min ago","llm_model":"GPT-5"},
  // {"agent_id":"copilot_flag_aggregation","agent_name":"Flag Aggregation Agent","planner_id":"planner_copilot","team_id":"copilot","core_module_mapping":"Claims Workbench + Configurator","shared_agent":true,"role":"Aggregate","status":"Active","last_active":"2 min ago","llm_model":"GPT-5"},
  // {"agent_id":"copilot_recommendation_narrative","agent_name":"Recommendation Narrative Agent","planner_id":"planner_copilot","team_id":"copilot","core_module_mapping":"Claims Workbench + Configurator","shared_agent":false,"role":"Recommend","status":"Active","last_active":"3 min ago","llm_model":"GPT-5"},
  // {"agent_id":"copilot_workbench_view_builder","agent_name":"Workbench View Builder Agent","planner_id":"planner_copilot","team_id":"copilot","core_module_mapping":"Claims Workbench + Configurator","shared_agent":false,"role":"UI","status":"Active","last_active":"4 min ago","llm_model":"GPT-5"},
  // {"agent_id":"communication_notification_trigger","agent_name":"Notification Trigger Agent","planner_id":"planner_communication","team_id":"communication","core_module_mapping":"Communication","shared_agent":false,"role":"Triggers notifications","status":"Inactive","last_active":"1 day ago","llm_model":"Claude-3"},
  // {"agent_id":"communication_template_population","agent_name":"Template Population Agent","planner_id":"planner_communication","team_id":"communication","core_module_mapping":"Communication","shared_agent":false,"role":"Populates templates","status":"Inactive","last_active":"1 day ago","llm_model":"Claude-3"},
  // {"agent_id":"communication_rfi","agent_name":"RFI Agent","planner_id":"planner_communication","team_id":"communication","core_module_mapping":"Communication","shared_agent":false,"role":"Handles RFIs","status":"Inactive","last_active":"1 day ago","llm_model":"Claude-3"},
  // {"agent_id":"communication_channel_dispatch","agent_name":"Channel Dispatch Agent","planner_id":"planner_communication","team_id":"communication","core_module_mapping":"Communication","shared_agent":false,"role":"Dispatches across channels","status":"Inactive","last_active":"1 day ago","llm_model":"Claude-3"},
  // {"agent_id":"closure_payment_trigger","agent_name":"Payment Trigger Agent","planner_id":"planner_closure_payment","team_id":"closure_payment","core_module_mapping":"Closure & Payment","shared_agent":false,"role":"Triggers payment","status":"Active","last_active":"30 min ago","llm_model":"Gemini-3"},
  // {"agent_id":"closure_payment_gl_tagging","agent_name":"GL Tagging Agent","planner_id":"planner_closure_payment","team_id":"closure_payment","core_module_mapping":"Closure & Payment","shared_agent":false,"role":"Tags GL entries","status":"Active","last_active":"35 min ago","llm_model":"Gemini-3"},
  // {"agent_id":"closure_payment_audit","agent_name":"Closure Audit Agent","planner_id":"planner_closure_payment","team_id":"closure_payment","core_module_mapping":"Closure & Payment","shared_agent":false,"role":"Audits closure","status":"Active","last_active":"40 min ago","llm_model":"Gemini-3"},
  // {"agent_id":"closure_payment_disbursement_confirmation","agent_name":"Disbursement Confirmation Agent","planner_id":"planner_closure_payment","team_id":"closure_payment","core_module_mapping":"Closure & Payment","shared_agent":false,"role":"Confirms disbursement","status":"Active","last_active":"45 min ago","llm_model":"Gemini-3"},
  // {"agent_id":"claims_summary_structured_extractor","agent_name":"Structured Data Extractor Agent","planner_id":"planner_claims_summary","team_id":"claims_summary","core_module_mapping":"Claims Summary","shared_agent":false,"role":"Extracts structured data","status":"Inactive","last_active":"2 days ago","llm_model":"GPT-5"},
  // {"agent_id":"claims_summary_unstructured_synthesiser","agent_name":"Unstructured Content Synthesiser Agent","planner_id":"planner_claims_summary","team_id":"claims_summary","core_module_mapping":"Claims Summary","shared_agent":false,"role":"Synthesises unstructured content","status":"Inactive","last_active":"2 days ago","llm_model":"GPT-5"},
  // {"agent_id":"claims_summary_narrative_generator","agent_name":"Narrative Generator Agent","planner_id":"planner_claims_summary","team_id":"claims_summary","core_module_mapping":"Claims Summary","shared_agent":false,"role":"Generates narratives","status":"Inactive","last_active":"2 days ago","llm_model":"GPT-5"},
  // {"agent_id":"claims_summary_escalation_brief","agent_name":"Escalation Brief Agent","planner_id":"planner_claims_summary","team_id":"claims_summary","core_module_mapping":"Claims Summary","shared_agent":false,"role":"Prepares escalation briefs","status":"Inactive","last_active":"2 days ago","llm_model":"GPT-5"}
];

const formatRelativeTime = (value) => {
  if (!value && value !== 0) return 'n/a';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'n/a';

  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffSeconds = Math.max(0, Math.floor(diffMs / 1000));
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);

  // If older than or equal to 6 hours, show absolute date-time
  if (diffHours >= 6) {
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  if (diffHours > 0) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  }

  if (diffMinutes > 0) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  }

  const seconds = Math.max(1, diffSeconds);
  return `${seconds} second${seconds === 1 ? '' : 's'} ago`;
};

const evaluateThresholdLevel = (value, rule) => {
  if (!rule || value == null) return null;
  const { warning, critical, direction } = rule;
  const compare = (v, limit) => (direction === 'below' ? v <= limit : v >= limit);
  if (critical != null && compare(value, critical)) return 'Critical';
  if (warning != null && compare(value, warning)) return 'Warning';
  return null;
};

const getSystemThresholdStatus = (row, config) => {
  const systemConfig = (config && config.system) || {};
  const metrics = [
    { key: 'cpu_pct', value: row.cpu_pct },
    { key: 'memory_pct', value: row.memory_pct },
    { key: 'latency_ms', value: row.latency_ms },
    { key: 'error_rate_pct', value: row.error_rate_pct },
    { key: 'uptime_pct', value: row.uptime_pct }
  ];
  let level = null;
  for (let i = 0; i < metrics.length; i += 1) {
    const m = metrics[i];
    const rule = systemConfig[m.key];
    const metricLevel = evaluateThresholdLevel(m.value, rule);
    if (metricLevel === 'Critical') return 'Critical';
    if (metricLevel === 'Warning' && !level) level = 'Warning';
  }
  return level;
};

const DEFAULT_THRESHOLD_CONFIG = {
  system: {
    cpu_pct: { warning: 70, critical: 85, direction: 'above' },
    memory_pct: { warning: 75, critical: 90, direction: 'above' },
    latency_ms: { warning: 150, critical: 300, direction: 'above' },
    error_rate_pct: { warning: 0.1, critical: 0.5, direction: 'above' },
    uptime_pct: { warning: 99, critical: 98, direction: 'below' }
  },
  entities: {
    teams: {
      success_rate_pct: { warning: 95, critical: 90, direction: 'below' }
    },
    planners: {
      success_rate_pct: { warning: 95, critical: 90, direction: 'below' }
    },
    agents: {
      success_rate_pct: { warning: 95, critical: 90, direction: 'below' }
    }
  }
};

const computeSuccessRate = (runs, statusKey = 'status') => {
  if (!runs || runs.length === 0) return null;
  let total = 0;
  let success = 0;
  runs.forEach((run) => {
    const s = String(run[statusKey] || '').toLowerCase();
    if (!s) return;
    total += 1;
    if (s === 'success') success += 1;
  });
  if (total === 0) return null;
  return (success / total) * 100;
};

const PAGE_SIZE = 10;

const getStatusBadgeStatus = (value) => {
  if (!value) return 'default';
  const v = String(value).toLowerCase();
  if (v === 'active' || v === 'healthy' || v === 'success') return 'active';
  if (v === 'warning' || v === 'running') return 'warning';
  if (v === 'critical' || v === 'failed') return 'error';
  return 'default';
};

const getSeverityBadgeStatus = (value) => {
  if (!value) return 'default';
  const v = String(value).toLowerCase();
  if (v === 'warning') return 'warning';
  if (v === 'critical') return 'error';
  return 'default';
};

// --- Small presentational helpers ---

const getSummaryCardColors = (title) => {
  const t = title.toLowerCase();

  if (t === 'services' || t === 'avg system uptime' || t === 'overall success rate') {
    return {
      background: 'rgba(52, 199, 89, 0.1)',
      valueColor: '#34C759'
    };
  }

  if (t === 'active alerts') {
    return {
      background: 'rgba(255, 204, 0, 0.1)',
      valueColor: '#FFCC00'
    };
  }

  // Default to primary blue tile, similar to Active Agents card
  return {
    background: 'rgba(0, 102, 255, 0.08)',
    valueColor: 'var(--primary)'
  };
};

const SummaryCards = ({ dateFilter }) => {
  const [loading, setLoading] = useState(false);
  const [totalCountData, setTotalCountData] = useState({
    totalPipelines: 0,
    totalAgents: 0,
    totalPlanners: 0,
    totalLLMTokens: 0,
    totalRuns: 0,
    pipelineRunCount: 0,
    agentRunCount: 0,
    plannerRunCount: 0,
    tokensPercentChange: null,
    runsPercentChange: null,
    successRate: null,
    successRatePercentChange: null,
    failedRuns: null,
    failedRunsPercentChange: null
  })

  // const cards = [
  //   { title: 'Teams', value: totalCountData?.totalPipelines },
  //   { title: 'Total Runs (all teams)', value: totalCountData?.totalPipelinesRun },
  //   { title: 'Overall Success Rate', value: `${overallSuccessRate.toFixed(1)}%` },
  //   { title: 'Total LLM Tokens', value: totalCountData?.totalLLMTokens },
  //   { title: 'Agents', value: totalCountData?.totalAgents },
  //   { title: 'Planners', value: totalCountData?.totalPlanners },
  //   { title: 'Services', value: totalServices },
  //   { title: 'Active Alerts', value: activeAlerts },
  //   { title: 'Avg System Uptime', value: `${avgUptime.toFixed(2)}%` }
  // ];

  const formatToK = (num) => {
    if (num < 1000) return String(num);

    const units = ['k', 'M', 'B', 'T'];
    let unitIndex = -1;

    while (num >= 1000 && unitIndex < units.length - 1) {
      num /= 1000;
      unitIndex++;
    }

    const formatted = num.toFixed(1);

    return formatted.endsWith('.0')
      ? formatted.slice(0, -2) + units[unitIndex]
      : formatted + units[unitIndex];
  }

  const tokensChange = totalCountData.tokensPercentChange;
  const tokensTrend = typeof tokensChange === 'number' && tokensChange < 0 ? 'down' : 'up';
  const tokensChangeLabel =
    typeof tokensChange === 'number'
      ? `${Math.abs(tokensChange).toFixed(1)}% vs last 24h`
      : 'vs last 24h';

  const runsChange = totalCountData.runsPercentChange;
  const runsTrend = typeof runsChange === 'number' && runsChange < 0 ? 'down' : 'up';
  const runsChangeLabel =
    typeof runsChange === 'number'
      ? `${Math.abs(runsChange).toFixed(1)}% vs last 24h`
      : 'vs last 24h';

  const successRateChange = totalCountData.successRatePercentChange;
  const successRateTrend = typeof successRateChange === 'number' && successRateChange < 0 ? 'down' : 'up';
  const successRateChangeLabel =
    typeof successRateChange === 'number'
      ? `${Math.abs(successRateChange).toFixed(2)}% vs last 24h`
      : 'vs last 24h';

  const failedRunsChange = totalCountData.failedRunsPercentChange;
  const failedRunsTrend = typeof failedRunsChange === 'number' && failedRunsChange < 0 ? 'down' : 'up';
  const failedRunsChangeLabel =
    typeof failedRunsChange === 'number'
      ? `${Math.abs(failedRunsChange).toFixed(1)}% vs last 24h`
      : 'vs last 24h';

  const metrics = [
    {
      label: "Total Runs",
      value: formatToK(totalCountData.totalRuns || 0),
      sub: "across all runs",
      change: runsChangeLabel,
      trend: runsTrend,
    },
    {
      label: "Tokens Consumed",
      value: formatToK(totalCountData.totalLLMTokens || 0),
      sub: "all metric runs",
      change: tokensChangeLabel,
      trend: tokensTrend,
    },
    {
      label: "Success Rate",
      value: typeof totalCountData.successRate === 'number'
        ? `${totalCountData.successRate.toFixed(2)}%`
        : '0.00%',
      sub: "excl. queued",
      change: successRateChangeLabel,
      trend: successRateTrend,
    },
    {
      label: "Failed Runs",
      value: typeof totalCountData.failedRuns === 'number'
        ? String(totalCountData.failedRuns)
        : '0',
      sub: "excl. queued",
      change: failedRunsChangeLabel,
      trend: failedRunsTrend,
    },
  ];

  useEffect(() => {
    const fetchCounts = async () => {
      setLoading(true);
      const df = dateFilter || {};
      const results = await Promise.allSettled([
        getEntityCount('agents'),
        getEntityCount('planners'),
        getEntityCount('pipeline'),
        // Per-type run counts (date-filtered)
        getObservabilityCount('pipeline', df),
        getObservabilityCount('agent', df),
        getObservabilityCount('planner', df),
        getTotalTokens(df),
        getTotalTokensPercentChangeLast24h(df),
        getTotalRunsCount(df),
        getRunCountPercentChangeLast24h(df),
        getSuccessRate(df),
        getSuccessRatePercentChangeLast24h(df),
        getFailedRuns(df),
        getFailedRunsPercentChangeLast24h(df)
      ]);

      const [
        agentRes,
        plannerRes,
        pipelineRes,
        pipelineRunCountRes,
        agentRunCountRes,
        plannerRunCountRes,
        totalTokensRes,
        tokensChangeRes,
        totalRunsRes,
        runsChangeRes,
        successRateRes,
        successRateChangeRes,
        failedRunsRes,
        failedRunsChangeRes
      ] = results;

      const agent = agentRes.status === 'fulfilled' ? agentRes.value : 0;
      const planner = plannerRes.status === 'fulfilled' ? plannerRes.value : 0;
      const pipeline = pipelineRes.status === 'fulfilled' ? pipelineRes.value : 0;
      const pipelineRunCount = pipelineRunCountRes.status === 'fulfilled' ? (pipelineRunCountRes.value || 0) : 0;
      const agentRunCount = agentRunCountRes.status === 'fulfilled' ? (agentRunCountRes.value || 0) : 0;
      const plannerRunCount = plannerRunCountRes.status === 'fulfilled' ? (plannerRunCountRes.value || 0) : 0;
      const totalTokens = totalTokensRes.status === 'fulfilled' ? totalTokensRes.value : 0;
      const tokensChangeData = tokensChangeRes.status === 'fulfilled' ? tokensChangeRes.value : null;
      const totalRuns = totalRunsRes.status === 'fulfilled' ? totalRunsRes.value : 0;
      const runsChangeData = runsChangeRes.status === 'fulfilled' ? runsChangeRes.value : null;
      const successRateData = successRateRes.status === 'fulfilled' ? successRateRes.value : null;
      const successRateChangeData = successRateChangeRes.status === 'fulfilled' ? successRateChangeRes.value : null;
      const failedRunsData = failedRunsRes.status === 'fulfilled' ? failedRunsRes.value : null;
      const failedRunsChangeData = failedRunsChangeRes.status === 'fulfilled' ? failedRunsChangeRes.value : null;

      setTotalCountData((prev) => ({
        ...prev,
        totalAgents: agent,
        totalPlanners: planner,
        totalPipelines: pipeline,
        pipelineRunCount,
        agentRunCount,
        plannerRunCount,
        totalLLMTokens: totalTokens,
        totalRuns,
        tokensPercentChange:
          tokensChangeData && typeof tokensChangeData.percent_change === 'number'
            ? tokensChangeData.percent_change
            : null,
        runsPercentChange:
          runsChangeData && typeof runsChangeData.percent_change === 'number'
            ? runsChangeData.percent_change
            : null,
        successRate:
          successRateData && typeof successRateData.success_rate === 'number'
            ? successRateData.success_rate
            : null,
        successRatePercentChange:
          successRateChangeData && typeof successRateChangeData.percent_change === 'number'
            ? successRateChangeData.percent_change
            : null,
        failedRuns:
          failedRunsData && typeof failedRunsData.failed_count === 'number'
            ? failedRunsData.failed_count
            : null,
        failedRunsPercentChange:
          failedRunsChangeData && typeof failedRunsChangeData.percent_change === 'number'
            ? failedRunsChangeData.percent_change
            : null
      }));
      setLoading(false);
    };

    fetchCounts();
  }, [dateFilter]);

  return (

    <>
      <CardsWrapper>
        {loading
          ? [0,1,2,3].map((i) => (
              <CardLayout key={i}>
                <div style={{ height: 18, width: 120, background: '#e5e7eb', borderRadius: 6 }} />
                <div style={{ height: 28, width: 80, background: '#e5e7eb', borderRadius: 6, margin: '10px 0' }} />
                <div style={{ height: 14, width: 140, background: '#f3f4f6', borderRadius: 6 }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <InlineSpinner />
                  <span style={{ color: '#6b7280', fontSize: 13 }}>Loading...</span>
                </div>
              </CardLayout>
            ))
          : metrics.map((m, i) => (
              <MetricCard key={i} {...m} />
            ))}
      </CardsWrapper>

      {/* Compact counts row: entities and per-type run counts */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff' }}>
          <strong style={{ color: '#111827', fontSize: 13, marginRight: 6 }}></strong>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><InlineSpinner /><span style={{ color: '#6b7280', fontSize: 13 }}>Loading...</span></div>
          ) : (
            <>
              <span style={{ color: '#374151', fontSize: 13 }}>Agents: <strong>{totalCountData.totalAgents}</strong></span>
              <span style={{ color: '#e5e7eb' }}>|</span>
              <span style={{ color: '#374151', fontSize: 13 }}>Pipelines: <strong>{totalCountData.totalPipelines}</strong></span>
              <span style={{ color: '#e5e7eb' }}>|</span>
              <span style={{ color: '#374151', fontSize: 13 }}>Planners: <strong>{totalCountData.totalPlanners}</strong></span>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff' }}>
          <strong style={{ color: '#111827', fontSize: 13, marginRight: 6 }}>Runs (filtered):</strong>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><InlineSpinner /><span style={{ color: '#6b7280', fontSize: 13 }}>Loading...</span></div>
          ) : (
            <>
              <span style={{ color: '#374151', fontSize: 13 }}>Pipeline: <strong>{totalCountData.pipelineRunCount}</strong></span>
              <span style={{ color: '#e5e7eb' }}>|</span>
              <span style={{ color: '#374151', fontSize: 13 }}>Agent: <strong>{totalCountData.agentRunCount}</strong></span>
              <span style={{ color: '#e5e7eb' }}>|</span>
              <span style={{ color: '#374151', fontSize: 13 }}>Planner: <strong>{totalCountData.plannerRunCount}</strong></span>
            </>
          )}
        </div>
      </div>
    </>
  );
};

// const SystemLoadChart = () => (
//   <div style={{ width: '100%', height: 260 }}>
//     <ResponsiveContainer width="100%" height="100%">
//       <LineChart data={SYSTEM_LOAD_BY_SERVICE} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
//         <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
//         <XAxis dataKey="label" stroke="#64748b" fontSize={12} />
//         <YAxis stroke="#64748b" fontSize={12} />
//         <Tooltip
//           contentStyle={{ fontSize: '0.8rem' }}
//           formatter={(value) => [`${value}%`, 'Load']}
//           labelFormatter={(label) => `Time: ${label}`}
//         />
//         <Legend
//           verticalAlign="top"
//           height={32}
//           iconType="line"
//           wrapperStyle={{ fontSize: '0.8rem' }}
//         />
//         {SYSTEM_HEALTH_DATA.map((svc, index) => (
//           <Line
//             key={svc.id}
//             type="monotone"
//             dataKey={svc.id}
//             name={svc.service}
//             stroke={SERVICE_COLORS[index % SERVICE_COLORS.length]}
//             strokeWidth={2}
//             dot={index === 0 ? { r: 3, stroke: SERVICE_COLORS[index % SERVICE_COLORS.length], strokeWidth: 1, fill: '#fff' } : false}
//             activeDot={{ r: 4 }}
//           />
//         ))}
//       </LineChart>
//     </ResponsiveContainer>
//   </div>
// );

const ConfigureThresholdModal = ({ isOpen, onClose, config, onSave }) => {
  const [draft, setDraft] = useState(config || DEFAULT_THRESHOLD_CONFIG);

  useEffect(() => {
    setDraft(config || DEFAULT_THRESHOLD_CONFIG);
  }, [config, isOpen]);

  const metrics = [
    { key: 'cpu_pct', label: 'CPU (%)', directionLabel: 'Above' },
    { key: 'memory_pct', label: 'Memory (%)', directionLabel: 'Above' },
    { key: 'latency_ms', label: 'Latency (ms)', directionLabel: 'Above' },
    { key: 'error_rate_pct', label: 'Error Rate (%)', directionLabel: 'Above' },
    { key: 'uptime_pct', label: 'Uptime (%)', directionLabel: 'Below' }
  ];

  const getMetricRule = (key) => {
    const system = (draft && draft.system) || {};
    const existing = system[key];
    if (existing) return existing;
    const defaults = DEFAULT_THRESHOLD_CONFIG.system[key] || {};
    return {
      warning: defaults.warning != null ? defaults.warning : null,
      critical: defaults.critical != null ? defaults.critical : null,
      direction: defaults.direction || 'above'
    };
  };

  const handleChange = (metricKey, field, rawValue) => {
    const value = rawValue === '' ? null : Number(rawValue);
    setDraft((prev) => {
      const prevConfig = prev || DEFAULT_THRESHOLD_CONFIG;
      const system = prevConfig.system || {};
      const currentRule = getMetricRule(metricKey);
      return {
        ...prevConfig,
        system: {
          ...system,
          [metricKey]: {
            ...currentRule,
            [field]: Number.isNaN(value) ? null : value
          }
        }
      };
    });
  };

  const handleSave = () => {
    const next = draft || DEFAULT_THRESHOLD_CONFIG;
    onSave(next);
    onClose();
  };

  const handleReset = () => {
    setDraft(DEFAULT_THRESHOLD_CONFIG);
    onSave(DEFAULT_THRESHOLD_CONFIG);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Configure Thresholds" size="large" hideFooter>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          fontSize: '0.9rem'
        }}
      >
        <div style={{ color: 'var(--text-secondary)' }}>
          Adjust thresholds for core system metrics. These values influence System Health status badges.
        </div>
        <Table>
          <thead>
            <tr>
              <TableHeader>Metric</TableHeader>
              <TableHeader>Warning</TableHeader>
              <TableHeader>Critical</TableHeader>
              <TableHeader>Direction</TableHeader>
            </tr>
          </thead>
          <tbody>
            {metrics.map((metric) => {
              const rule = getMetricRule(metric.key);
              return (
                <TableRow key={metric.key}>
                  <TableCell>{metric.label}</TableCell>
                  <TableCell>
                    <input
                      type="number"
                      value={rule.warning != null ? rule.warning : ''}
                      onChange={(e) => handleChange(metric.key, 'warning', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.25rem 0.4rem',
                        fontSize: '0.85rem',
                        borderRadius: '4px',
                        border: '1px solid #e2e8f0'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <input
                      type="number"
                      value={rule.critical != null ? rule.critical : ''}
                      onChange={(e) => handleChange(metric.key, 'critical', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.25rem 0.4rem',
                        fontSize: '0.85rem',
                        borderRadius: '4px',
                        border: '1px solid #e2e8f0'
                      }}
                    />
                  </TableCell>
                  <TableCell>{metric.directionLabel}</TableCell>
                </TableRow>
              );
            })}
          </tbody>
        </Table>
        <div style={{ marginTop: '1rem', fontWeight: 600 }}>Entity success-rate thresholds</div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>
          Configure minimum success rate for Teams, Planners, and Agents. Below Warning/Critical levels will mark entities as Warning/Critical.
        </div>
        <Table>
          <thead>
            <tr>
              <TableHeader>Entity</TableHeader>
              <TableHeader>Warning success rate (%)</TableHeader>
              <TableHeader>Critical success rate (%)</TableHeader>
            </tr>
          </thead>
          <tbody>
            {['teams', 'planners', 'agents'].map((entityKey) => {
              const entitiesConfig = (draft && draft.entities) || {};
              const entityRules = entitiesConfig[entityKey] || {};
              const rule = entityRules.success_rate_pct || DEFAULT_THRESHOLD_CONFIG.entities[entityKey].success_rate_pct;
              const label =
                entityKey === 'teams' ? 'Teams' : entityKey === 'planners' ? 'Planners' : 'Agents';
              const handleEntityChange = (field, rawValue) => {
                const value = rawValue === '' ? null : Number(rawValue);
                setDraft((prev) => {
                  const base = prev || DEFAULT_THRESHOLD_CONFIG;
                  const prevEntities = base.entities || {};
                  const prevEntityRules = prevEntities[entityKey] || {};
                  const currentRule = prevEntityRules.success_rate_pct || DEFAULT_THRESHOLD_CONFIG.entities[entityKey].success_rate_pct;
                  return {
                    ...base,
                    entities: {
                      ...prevEntities,
                      [entityKey]: {
                        ...prevEntityRules,
                        success_rate_pct: {
                          ...currentRule,
                          [field]: Number.isNaN(value) ? null : value
                        }
                      }
                    }
                  };
                });
              };
              return (
                <TableRow key={entityKey}>
                  <TableCell>{label} Kunal</TableCell>
                  <TableCell>
                    <input
                      type="number"
                      value={rule.warning != null ? rule.warning : ''}
                      onChange={(e) => handleEntityChange('warning', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.25rem 0.4rem',
                        fontSize: '0.85rem',
                        borderRadius: '4px',
                        border: '1px solid #e2e8f0'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <input
                      type="number"
                      value={rule.critical != null ? rule.critical : ''}
                      onChange={(e) => handleEntityChange('critical', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.25rem 0.4rem',
                        fontSize: '0.85rem',
                        borderRadius: '4px',
                        border: '1px solid #e2e8f0'
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </tbody>
        </Table>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.5rem',
            marginTop: '0.5rem'
          }}
        >
          <Button
            style={{ padding: '0.35rem 0.8rem', fontSize: '0.85rem' }}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            style={{ padding: '0.35rem 0.8rem', fontSize: '0.85rem' }}
            onClick={handleReset}
          >
            Reset to defaults
          </Button>
          <Button
            primary
            style={{ padding: '0.35rem 0.9rem', fontSize: '0.85rem' }}
            onClick={handleSave}
          >
            Save thresholds
          </Button>
        </div>
      </div>
    </Modal>
  );
};


const KeyInsights = () => {


  const [activeChartTab, setActiveChartTab] = useState("trends");

  const INSIGHTS = [
    { title: "3 failed runs in last 24 hrs", sub: "Across 2 agents teams" },
    { title: "OCR timeout spike detected", sub: "Document processor showing high failure rate" },
    { title: "Token usage up by 18%", sub: "Compared to yesterday" },
    { title: "2 SLA breaches", sub: "Claims intake and adjudication pipelines" },
  ];

  return (
    <Wrapper>
      {/* Left */}
      <LeftPanel>
        <PanelHeader>
          <HeaderIcon>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="2" y="4" width="12" height="1.5" rx="0.75" fill="#6b7280" />
              <rect x="2" y="7.25" width="12" height="1.5" rx="0.75" fill="#6b7280" />
              <rect x="2" y="10.5" width="12" height="1.5" rx="0.75" fill="#6b7280" />
            </svg>
          </HeaderIcon>
          <HeaderTitle>Key Insights</HeaderTitle>
        </PanelHeader>

        <InsightList>
          {INSIGHTS.map((item, i) => (
            <InsightItem key={i}>
              <InsightAvatar />
              <InsightText>
                <InsightTitle>{item.title}</InsightTitle>
                <InsightSub>{item.sub}</InsightSub>
              </InsightText>
            </InsightItem>
          ))}
        </InsightList>

        <ViewAllBtn>View All Insights</ViewAllBtn>
      </LeftPanel>

      {/* Right */}
      <RightPanel>
        <TabRow>
          <TabGroup>
            <Tab active={activeChartTab === "trends"} onClick={() => setActiveChartTab("trends")}>
              System Trends
            </Tab>
            <Tab active={activeChartTab === "health"} onClick={() => setActiveChartTab("health")}>
              System Health
            </Tab>
          </TabGroup>
          <RangeSelect>
            <option>Last 24 hrs</option>
            <option>Last 7 days</option>
            <option>Last 30 days</option>
          </RangeSelect>
        </TabRow>

        <LegendRow>
          <LegendItem><LegendDot color="#29b6f6" /> Total Runs</LegendItem>
          <LegendItem><LegendDot color="#ef5350" /> Failed Runs</LegendItem>
        </LegendRow>

        <SystemTrendChart activeTab={activeChartTab} />
      </RightPanel>
    </Wrapper>
  )
}

// --- Main component ---

const transformTimelineToGraphData = (apiResponse) => {
  if (!apiResponse || apiResponse.length === 0) {
    return {
      labels: [],
      totalData: [],
      failedData: [],
      totalLabel: "Total Runs",
      failedLabel: "Failed Runs",
      maxValue: 10,
      stepSize: 2
    };
  }

  const timeMap = new Map();
  
  apiResponse.forEach(item => {
    const timestamp = item.bucket_start_ms;
    if (!timeMap.has(timestamp)) {
      timeMap.set(timestamp, {
        timestamp,
        totalCount: 0,
        failedCount: 0
      });
    }
    
    const entry = timeMap.get(timestamp);
    entry.totalCount += item.count;
    entry.failedCount += item.failed_count;
  });

  const sortedEntries = Array.from(timeMap.values())
    .sort((a, b) => a.timestamp - b.timestamp);

  const labels = sortedEntries.map(entry => {
    const date = new Date(entry.timestamp);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${month}/${day} ${hours}:${minutes}`;
  });

  const totalData = sortedEntries.map(entry => entry.totalCount);
  const failedData = sortedEntries.map(entry => entry.failedCount);

  const maxCount = Math.max(...totalData, 10);
  const maxValue = Math.ceil(maxCount * 1.2 / 10) * 10;
  const stepSize = Math.ceil(maxValue / 5);

  return {
    labels,
    totalData,
    failedData,
    totalLabel: "Total Runs",
    failedLabel: "Failed Runs",
    maxValue,
    stepSize
  };
};

const ObservabilityV2 = () => {
  const navigate = useNavigate();
  const [systemTab, setSystemTab] = useState('health');
  const [teamsTab, setTeamsTab] = useState('teams');
  const [plannersTab, setPlannersTab] = useState('planners');
  const [agentsTab, setAgentsTab] = useState('agents');

  // Performance card tabs
  const [agentPerfTab, setAgentPerfTab] = useState('agents');
  const [teamPerfTab, setTeamPerfTab] = useState('teams');
  const [plannerPerfTab, setPlannerPerfTab] = useState('planners');

  // Search & pagination state per table (10 records per page)
  const [systemHealthSearch, setSystemHealthSearch] = useState('');
  const [systemHealthPage, setSystemHealthPage] = useState(1);

  const [alertsSearch, setAlertsSearch] = useState('');
  const [alertsPage, setAlertsPage] = useState(1);

  const [teamsSearch, setTeamsSearch] = useState('');
  const [teamsPage, setTeamsPage] = useState(1);

  const [teamRunsSearch, setTeamRunsSearch] = useState('');

  const [plannersSearch, setPlannersSearch] = useState('');
  const [plannersPage, setPlannersPage] = useState(1);

  const [plannerRunsSearch, setPlannerRunsSearch] = useState('');

  const [agentsSearch, setAgentsSearch] = useState('');
  const [agentsPage, setAgentsPage] = useState(1);

  const [agentRunsSearch, setAgentRunsSearch] = useState('');

  // Date range filter state (default: today)
  const initialRange = useMemo(() => ([{
    startDate: new Date(new Date().setHours(0,0,0,0)),
    endDate: new Date(new Date().setHours(23,59,59,999)),
    key: 'selection'
  }]), []);
  // Applied range: drives all API calls
  const [appliedRange, setAppliedRange] = useState(initialRange);
  // Draft range: edited in the picker; only applied on button click
  const [draftRange, setDraftRange] = useState(initialRange);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef(null);
  const dateFilterMs = useMemo(() => {
    const start = appliedRange?.[0]?.startDate ? new Date(appliedRange[0].startDate) : new Date();
    const end = appliedRange?.[0]?.endDate ? new Date(appliedRange[0].endDate) : new Date();
    const $gte = start.getTime();
    const $lte = end.getTime();
    return { started_at: { $gte, $lte } };
  }, [appliedRange]);

  const rangeLabel = useMemo(() => {
    const start = appliedRange?.[0]?.startDate ? new Date(appliedRange[0].startDate) : new Date();
    const end = appliedRange?.[0]?.endDate ? new Date(appliedRange[0].endDate) : new Date();
    const today = new Date();
    const isToday = isSameDay(start, today) && isSameDay(end, today);
    return isToday ? 'Today' : `${format(start, 'MMM d, yyyy')} - ${format(end, 'MMM d, yyyy')}`;
  }, [appliedRange]);

  useEffect(() => {
    const onDown = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setShowDatePicker(false);
      }
    };
    if (showDatePicker) document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showDatePicker]);

  // Sync draft to applied when opening the picker
  useEffect(() => {
    if (showDatePicker) {
      setDraftRange(appliedRange);
    }
  }, [showDatePicker]);

  useEffect(() => {
    // Expose global for SummaryCards without prop drilling
    window.__obsDateFilter = dateFilterMs;
  }, [dateFilterMs]);

  const [teamsData, setTeamsData] = useState(TEAMS_DATA);
  const [plannersData, setPlannersData] = useState(PLANNERS_DATA);
  const [agentsData, setAgentsData] = useState(AGENTS_DATA);

  const [teamRunsData, setTeamRunsData] = useState(TEAM_RUNS_DATA);
  const [plannerRunsData, setPlannerRunsData] = useState(PLANNER_RUNS_DATA);
  const [agentSessionsData, setAgentSessionsData] = useState(AGENT_SESSIONS_DATA);
  // API-driven runs data and pagination (skip/limit)
  const [teamRunsApiData, setTeamRunsApiData] = useState([]);
  const [teamRunsLoading, setTeamRunsLoading] = useState(false);
  const [teamRunsError, setTeamRunsError] = useState(null);
  const [teamRunsHasNext, setTeamRunsHasNext] = useState(false);

  const [plannerRunsApiData, setPlannerRunsApiData] = useState([]);
  const [plannerRunsLoading, setPlannerRunsLoading] = useState(false);
  const [plannerRunsError, setPlannerRunsError] = useState(null);
  const [plannerRunsHasNext, setPlannerRunsHasNext] = useState(false);

  const [agentRunsApiData, setAgentRunsApiData] = useState([]);
  const [agentRunsLoading, setAgentRunsLoading] = useState(false);
  const [agentRunsError, setAgentRunsError] = useState(null);
  const [agentRunsHasNext, setAgentRunsHasNext] = useState(false);
  
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [plannersLoading, setPlannersLoading] = useState(false);
  const [agentGraphLoading, setAgentGraphLoading] = useState(false);
  const [teamGraphLoading, setTeamGraphLoading] = useState(false);
  const [plannerGraphLoading, setPlannerGraphLoading] = useState(false);
  
  const [agentGraphData, setAgentGraphData] = useState({
    labels: [],
    totalData: [],
    failedData: [],
    maxValue: 10,
    stepSize: 2
  });
  
  const [teamGraphData, setTeamGraphData] = useState({
    labels: [],
    totalData: [],
    failedData: [],
    maxValue: 10,
    stepSize: 2
  });
  
  const [plannerGraphData, setPlannerGraphData] = useState({
    labels: [],
    totalData: [],
    failedData: [],
    maxValue: 10,
    stepSize: 2
  });
  
  const [thresholdConfig, setThresholdConfig] = useState(DEFAULT_THRESHOLD_CONFIG);
  const [thresholdsOpen, setThresholdsOpen] = useState(false);
  const teamRunPagination = usePagination();
  const plannerRunPagination = usePagination();
  const agentRunPagination = usePagination();
  const teamPagination = usePagination();



  useEffect(() => {
    try {
      const stored = localStorage.getItem('observabilityV2.thresholds');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === 'object') {
          setThresholdConfig((prev) => ({
            ...prev,
            ...parsed,
            system: {
              ...(prev.system || {}),
              ...((parsed && parsed.system) || {})
            }
          }));
        }
      }
    } catch (e) {
    }
  }, []);


  useEffect(() => {
    const fetchCounts = async () => {
      const results = await Promise.allSettled([
        getObservabilityCount('pipeline', dateFilterMs),
        getObservabilityCount('planner', dateFilterMs),
        getObservabilityCount('agent', dateFilterMs),
        getEntityCount('pipeline')
      ]);

      const [exePipelineRes, exePlannerRes, exeAgentRes, pipelinesRes] = results;
      const exePipeline = exePipelineRes.status === "fulfilled" ? exePipelineRes.value : 0;
      const planners = exePlannerRes.status === "fulfilled" ? exePlannerRes.value : 0;
      const agents = exeAgentRes.status === "fulfilled" ? exeAgentRes.value : 0;
      const pipelines = exeAgentRes.status === "fulfilled" ? pipelinesRes.value : 0;

      teamRunPagination?.setToatalItems(exePipeline)
      plannerRunPagination?.setToatalItems(planners)
      agentRunPagination?.setToatalItems(agents)
      teamPagination?.setToatalItems(pipelines)
    };

    fetchCounts();

  }, [dateFilterMs])

  useEffect(() => {
    try {
      localStorage.setItem('observabilityV2.thresholds', JSON.stringify(thresholdConfig));
    } catch (e) {
    }
  }, [thresholdConfig]);

  const deactivateTeam = (teamId) => {
    setTeamsData((prev) =>
      prev.map((team) =>
        team.team_id === teamId && String(team.status).toLowerCase() === 'active'
          ? { ...team, status: 'Inactive' }
          : team
      )
    );
  };

  const deactivatePlanner = (plannerId) => {
    setPlannersData((prev) =>
      prev.map((planner) =>
        planner.planner_id === plannerId && String(planner.status).toLowerCase() === 'active'
          ? { ...planner, status: 'Inactive' }
          : planner
      )
    );
  };

  const deactivateAgent = (agentId) => {
    setAgentsData((prev) =>
      prev.map((agent) =>
        agent.agent_id === agentId && String(agent.status).toLowerCase() === 'active'
          ? { ...agent, status: 'Inactive' }
          : agent
      )
    );
  };

  const activateTeam = (teamId) => {
    setTeamsData((prev) =>
      prev.map((team) =>
        team.team_id === teamId && String(team.status).toLowerCase() === 'inactive'
          ? { ...team, status: 'Active' }
          : team
      )
    );
  };

  const activatePlanner = (plannerId) => {
    setPlannersData((prev) =>
      prev.map((planner) =>
        planner.planner_id === plannerId && String(planner.status).toLowerCase() === 'inactive'
          ? { ...planner, status: 'Active' }
          : planner
      )
    );
  };

  const activateAgent = (agentId) => {
    setAgentsData((prev) =>
      prev.map((agent) =>
        agent.agent_id === agentId && String(agent.status).toLowerCase() === 'inactive'
          ? { ...agent, status: 'Active' }
          : agent
      )
    );
  };

  const pauseTeamRun = (teamRunId) => {
    setTeamRunsData((prev) =>
      prev.map((run) =>
        run.team_run_id === teamRunId && String(run.status).toLowerCase() === 'running'
          ? { ...run, status: 'Paused' }
          : run
      )
    );
  };

  const retryTeamRun = (teamRunId) => {
    setTeamRunsData((prev) =>
      prev.map((run) => {
        const v = String(run.status).toLowerCase();
        if (run.team_run_id === teamRunId && (v === 'failed' || v === 'success')) {
          return {
            ...run,
            status: 'running',
            finished_at: null,
            duration_ms: null
          };
        }
        return run;
      })
    );
  };

  // --- Backend fetch helpers for runs ---

  const fetchTeamRuns = async (skipValue = 0) => {
    setTeamRunsLoading(true);
    setTeamRunsError(null);
    try {
      const body = {
        filter: { ...dateFilterMs },
        skip: skipValue,
        limit: PAGE_SIZE
      };
      const data = await post('/api/v1/mms_ai_metric_events/metrics/runs/pipeline', body);
      // Expecting an array of MetricRun objects
      const runs = Array.isArray(data) ? data : [];
      console.log("Team/Pipeline runs data:", runs)
      setTeamRunsApiData(runs);
      setTeamRunsHasNext(runs.length === PAGE_SIZE);
    } catch (e) {
      // Keep previous data on error, just surface the error state
      setTeamRunsError('Failed to load team (pipeline) runs');
      setTeamRunsHasNext(false);
    } finally {
      setTeamRunsLoading(false);
    }
  };


  const fetchTeams = async (skipValue = 0) => {
    setTeamsLoading(true);
    try {
      const body = {
        filter: { ...dateFilterMs },
        skip: skipValue,
        limit: PAGE_SIZE
      };

      const data = await post('/api/v1/mms_ai_metric_events/metrics/runs/configs_with_aggregate_metrics/pipeline', body);
      const teams = Array.isArray(data) ? data : [];
      console.log("Teams/Pipeline configs data:", teams)

      // Update teamsData with the configs from the API response
      if (teams.length > 0 && teams[0].config) {
        const teamConfigs = teams.map(item => ({
          ...item.config,
          team_id: item.config.code,
          team_name: item.config.name || item.config.displayName,
          pipeline_name: item.config.name || item.config.displayName,
          total_runs: item.total_runs,
          total_tokens: item.total_tokens,
          success_rate: item.success_rate,
          average_execution_time: item.average_execution_time,
          agent_names: item.agent_names,
          planner_names: item.planner_names,
          status: item.total_runs > 0 ? 'Active' : 'Inactive'
        }));
        setTeamsData(teamConfigs);
      }
    } catch (error) {
      console.log("Error fetching teams:", error)
    } finally {
      setTeamsLoading(false);
    }
  }



  const fetchPlanners = async (skipValue = 0) => {
    setPlannersLoading(true);
    try {
      const body = {
        filter: { ...dateFilterMs },
        skip: skipValue,
        limit: PAGE_SIZE
      };

      const data = await post('/api/v1/mms_ai_metric_events/metrics/runs/configs_with_aggregate_metrics/planner', body);
      const planners = Array.isArray(data) ? data : [];
      console.log("Planner configs data:", planners)

      // Update plannersData with the configs from the API response
      if (planners.length > 0 && planners[0].config) {
        const plannerConfigs = planners.map(item => ({
          ...item.config,
          planner_id: item.config.code,
          planner_name: item.config.name || item.config.displayName,
          total_runs: item.total_runs,
          total_tokens: item.total_tokens,
          success_rate: item.success_rate,
          average_execution_time: item.average_execution_time,
          agent_names: item.agent_names,
          pipeline_names: item.pipeline_names,
          status: item.total_runs > 0 ? 'Active' : 'Inactive'
        }));
        setPlannersData(plannerConfigs);
      }
    } catch (error) {
      console.log("Error fetching planners:", error)
    } finally {
      setPlannersLoading(false);
    }
  }

  const fetchPlannerRuns = async (skipValue = 0) => {
    setPlannerRunsLoading(true);
    setPlannerRunsError(null);
    try {
      const body = {
        filter: { ...dateFilterMs },
        skip: skipValue,
        limit: PAGE_SIZE
      };
      const data = await post('/api/v1/mms_ai_metric_events/metrics/runs/planner', body);
      const runs = Array.isArray(data) ? data : [];
      console.log("Planner runs data:", runs)
      setPlannerRunsApiData(runs);
      setPlannerRunsHasNext(runs.length === PAGE_SIZE);
    } catch (e) {
      setPlannerRunsError('Failed to load planner runs');
      setPlannerRunsHasNext(false);
    } finally {
      setPlannerRunsLoading(false);
    }
  };

  const fetchAgents = async (skipValue = 0) => {
    setAgentsLoading(true);
    try {
      const body = {
        filter: { ...dateFilterMs },
        skip: skipValue,
        limit: PAGE_SIZE
      };

      const data = await post('/api/v1/mms_ai_metric_events/metrics/runs/configs_with_aggregate_metrics/agent', body);
      const agents = Array.isArray(data) ? data : [];
      console.log("Agents configs data:", agents)

      // Update agentsData with the configs from the API response
      if (agents.length > 0 && agents[0].config) {
        const agentConfigs = agents.map(item => ({
          ...item.config,
          agent_id: item.config.code,
          agent_name: item.config.name || item.config.displayName,
          total_runs: item.total_runs,
          total_tokens: item.total_tokens,
          success_rate: item.success_rate,
          average_execution_time: item.average_execution_time,
          pipeline_names: item.pipeline_names,
          planner_names: item.planner_names,
          status: item.total_runs > 0 ? 'Active' : 'Inactive'
        }));
        setAgentsData(agentConfigs);
      }
    } catch (error) {
      console.log("Error fetching agents:", error)
    } finally {
      setAgentsLoading(false);
    }
  }

  const fetchAgentRuns = async (skipValue = 0) => {
    setAgentRunsLoading(true);
    setAgentRunsError(null);
    try {
      const body = {
        filter: { ...dateFilterMs },
        skip: skipValue,
        limit: PAGE_SIZE
      };
      const data = await post('/api/v1/mms_ai_metric_events/metrics/runs/agent', body);
      const runs = Array.isArray(data) ? data : [];
      console.log("Agent runs data:", runs)
      setAgentRunsApiData(runs);
      setAgentRunsHasNext(runs.length === PAGE_SIZE);
    } catch (e) {
      setAgentRunsError('Failed to load agent runs');
      setAgentRunsHasNext(false);
    } finally {
      setAgentRunsLoading(false);
    }
  };

  const fetchAgentTimeline = async () => {
    setAgentGraphLoading(true);
    try {
      const response = await post('/api/v1/mms_ai_metric_events/metrics/runs/count_timeline/agent', { ...dateFilterMs });
      const data = Array.isArray(response) ? response : [];
      const graphData = transformTimelineToGraphData(data);
      setAgentGraphData(graphData);
    } catch (error) {
      console.error('Error fetching agent timeline:', error);
    } finally {
      setAgentGraphLoading(false);
    }
  };

  const fetchTeamTimeline = async () => {
    setTeamGraphLoading(true);
    try {
      const response = await post('/api/v1/mms_ai_metric_events/metrics/runs/count_timeline/pipeline', { ...dateFilterMs });
      const data = Array.isArray(response) ? response : [];
      const graphData = transformTimelineToGraphData(data);
      setTeamGraphData(graphData);
    } catch (error) {
      console.error('Error fetching team timeline:', error);
    } finally {
      setTeamGraphLoading(false);
    }
  };

  const fetchPlannerTimeline = async () => {
    setPlannerGraphLoading(true);
    try {
      const response = await post('/api/v1/mms_ai_metric_events/metrics/runs/count_timeline/planner', { ...dateFilterMs });
      const data = Array.isArray(response) ? response : [];
      const graphData = transformTimelineToGraphData(data);
      setPlannerGraphData(graphData);
    } catch (error) {
      console.error('Error fetching planner timeline:', error);
    } finally {
      setPlannerGraphLoading(false);
    }
  };

  console.log("Run data:",)

  // Initial load for all three run types
  useEffect(() => {
    // Reset to first page on date change for all lists
    teamRunPagination?.setCurrentPage(1);
    plannerRunPagination?.setCurrentPage(1);
    agentRunPagination?.setCurrentPage(1);
    teamPagination?.setCurrentPage(1);

    fetchTeams(0);
    fetchPlanners(0);
    fetchAgents(0);
  }, [dateFilterMs]);

  useEffect(() => {
    fetchAgentTimeline();
    fetchTeamTimeline();
    fetchPlannerTimeline();
  }, [dateFilterMs]);


  useEffect(() => {
    fetchTeamRuns((teamRunPagination?.currentPage - 1) * PAGE_SIZE);
  }, [teamRunPagination?.currentPage])

  useEffect(() => {
    fetchPlannerRuns((plannerRunPagination?.currentPage - 1) * PAGE_SIZE);
  }, [plannerRunPagination?.currentPage])


  useEffect(() => {
    fetchAgentRuns((agentRunPagination?.currentPage - 1) * PAGE_SIZE);
  }, [agentRunPagination?.currentPage])

  useEffect(() => {
    fetchTeams((teamPagination?.currentPage - 1) * PAGE_SIZE);
  }, [teamPagination?.currentPage])


  // Pagination & search handlers for API-backed runs

  const handleTeamRunsSearchChange = (e) => {
    const value = e.target.value;
    setTeamRunsSearch(value);
    const resetSkip = 0;
    const resetPage = 1;

    fetchTeamRuns(resetSkip);
  };

  const handlePlannerRunsSearchChange = (e) => {
    const value = e.target.value;
    setPlannerRunsSearch(value);
    const resetSkip = 0;
    const resetPage = 1;

    fetchPlannerRuns(resetSkip);
  };


  const handleAgentRunsSearchChange = (e) => {
    const value = e.target.value;
    setAgentRunsSearch(value);
    const resetSkip = 0;
    const resetPage = 1;

    fetchAgentRuns(resetSkip);
  };


  const pausePlannerRun = (plannerRunId) => {
    setPlannerRunsData((prev) =>
      prev.map((run) =>
        run.planner_run_id === plannerRunId && String(run.status).toLowerCase() === 'running'
          ? { ...run, status: 'Paused' }
          : run
      )
    );
  };

  const retryPlannerRun = (plannerRunId) => {
    setPlannerRunsData((prev) =>
      prev.map((run) => {
        const v = String(run.status).toLowerCase();
        if (run.planner_run_id === plannerRunId && (v === 'failed' || v === 'success')) {
          return {
            ...run,
            status: 'running',
            finished_at: null,
            duration_ms: null
          };
        }
        return run;
      })
    );
  };

  const pauseAgentSession = (sessionId) => {
    setAgentSessionsData((prev) =>
      prev.map((session) =>
        session.agent_session_id === sessionId && String(session.status).toLowerCase() === 'running'
          ? { ...session, status: 'Paused' }
          : session
      )
    );
  };

  const retryAgentSession = (sessionId) => {
    setAgentSessionsData((prev) =>
      prev.map((session) => {
        const v = String(session.status).toLowerCase();
        if (session.agent_session_id === sessionId && (v === 'failed' || v === 'success')) {
          return {
            ...session,
            status: 'running',
            finished_at: null,
            duration_ms: null
          };
        }
        return session;
      })
    );
  };

  // Details modal state
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsTitle, setDetailsTitle] = useState('');
  const [detailsFields, setDetailsFields] = useState([]);
  const [detailsToolCalls, setDetailsToolCalls] = useState(null);
  const [detailsToolCallsType, setDetailsToolCallsType] = useState(null); // 'planner' | 'agent' | null

  const openDetailsModal = (title, fields, toolCalls = null, toolCallsType = null) => {
    setDetailsTitle(title);
    setDetailsFields(fields);
    setDetailsToolCalls(toolCalls);
    setDetailsToolCallsType(toolCallsType);
    setDetailsOpen(true);
  };

  // --- Derived filtered + paginated data sets ---

  const term = (value) => value.trim().toLowerCase();

  // System Health
  const systemHealthFiltered = SYSTEM_HEALTH_DATA.filter((row) => {
    const q = term(systemHealthSearch);
    if (!q) return true;
    return (
      String(row.id).toLowerCase().includes(q) ||
      String(row.service).toLowerCase().includes(q) ||
      String(row.status).toLowerCase().includes(q)
    );
  });
  const systemHealthTotalPages = Math.max(1, Math.ceil(systemHealthFiltered.length / PAGE_SIZE));
  const systemHealthCurrentPage = Math.min(systemHealthPage, systemHealthTotalPages);
  const systemHealthStart = (systemHealthCurrentPage - 1) * PAGE_SIZE;
  const systemHealthRows = systemHealthFiltered.slice(systemHealthStart, systemHealthStart + PAGE_SIZE);
  const systemHealthRowsWithThresholds = systemHealthRows.map((row) => {
    const level = getSystemThresholdStatus(row, thresholdConfig);
    return level
      ? { ...row, effective_status: level }
      : { ...row, effective_status: row.status };
  });

  // Alerts
  const alertsFiltered = ALERTS_DATA.filter((alert) => {
    const q = term(alertsSearch);
    if (!q) return true;
    const text = [
      alert.id,
      alert.timestamp,
      alert.service,
      alert.message,
      alert.severity,
      alert.status
    ]
      .join(' ')
      .toLowerCase();
    return text.includes(q);
  });
  const alertsTotalPages = Math.max(1, Math.ceil(alertsFiltered.length / PAGE_SIZE));
  const alertsCurrentPage = Math.min(alertsPage, alertsTotalPages);
  const alertsStart = (alertsCurrentPage - 1) * PAGE_SIZE;
  const alertsRows = alertsFiltered.slice(alertsStart, alertsStart + PAGE_SIZE);

  // Teams
  const teamsFiltered = teamsData.filter((team) => {
    const q = term(teamsSearch);
    if (!q) return true;
    const planner = PLANNERS_DATA.find((p) => p.planner_id === team.planner_id);

    // Handle both old and new API structures
    const plannerNamesStr = team.planner_names ? team.planner_names.join(' ') : '';
    const agentNamesStr = team.agent_names ? team.agent_names.join(' ') : '';

    const text = [
      team.team_name,
      team.pipeline_name,
      team.name, // New API field
      team.description, // New API field
      planner ? planner.planner_name : '',
      plannerNamesStr,
      agentNamesStr,
      team.domain_context,
      team.status,
      team.last_active
    ]
      .join(' ')
      .toLowerCase();
    return text.includes(q);
  });
  const teamsTotalPages = Math.max(1, Math.ceil(teamsFiltered.length / PAGE_SIZE));
  const teamsCurrentPage = Math.min(teamsPage, teamsTotalPages);
  const teamsStart = (teamsCurrentPage - 1) * PAGE_SIZE;
  const teamsRowsBase = teamsFiltered.slice(teamsStart, teamsStart + PAGE_SIZE);

  const teamSuccessRule =
    (thresholdConfig.entities && thresholdConfig.entities.teams && thresholdConfig.entities.teams.success_rate_pct) ||
    DEFAULT_THRESHOLD_CONFIG.entities.teams.success_rate_pct;

  const teamsRows = teamsRowsBase.map((team) => {
    // Check if this is the new API format with config object
    if (team.config) {
      // New API format: extract config and merge with aggregate metrics
      const { config, total_runs, total_tokens, success_rate, average_execution_time, agent_names, planner_names } = team;
      const successRate = success_rate || 0;
      const level = successRate != null ? evaluateThresholdLevel(successRate, teamSuccessRule) : null;
      const effective_status = level || (total_runs > 0 ? 'active' : 'inactive');

      return {
        ...config,
        team_id: config.code,
        team_name: config.name || config.displayName,
        pipeline_name: config.name || config.displayName,
        description: config.description,
        total_runs,
        total_tokens,
        success_rate: successRate,
        average_execution_time,
        agent_names,
        planner_names,
        effective_status,
        status: effective_status
      };
    } else {
      // Old format: use session-based calculation
      const teamRuns = teamRunsData.filter((run) => run.team_id === team.team_id);
      const successRate = computeSuccessRate(teamRuns);
      const level = successRate != null ? evaluateThresholdLevel(successRate, teamSuccessRule) : null;
      const effective_status = level || team.status;
      return { ...team, effective_status };
    }
  });

  // Team Runs (API-driven)
  const teamRunsSource = teamRunsApiData.length ? teamRunsApiData : teamRunsData;
  const teamRunsFiltered = teamRunsSource.filter((run) => {
    const q = term(teamRunsSearch);
    if (!q) return true;

    // Handle new API structure with config object
    const runData = run.config ? {
      team_id: run.config.code,
      team_run_id: run.config.code,
      run_id: run.config.code,
      metric_type_name: run.config.name,
      status: run.total_runs > 0 ? 'Active' : 'Inactive',
      started_at: run.config.created_at,
      ...run
    } : run;

    const team = TEAMS_DATA.find((t) => t.team_id === runData.team_id);
    const pipelineName = runData.metric_type_name || (team ? team.pipeline_name || team.team_name : '');
    const pipelineNamesStr = run.pipeline_names ? run.pipeline_names.join(' ') : '';

    const text = [
      runData.team_run_id || runData.run_id,
      pipelineName,
      pipelineNamesStr,
      runData.status,
      runData.started_at
    ]
      .join(' ')
      .toLowerCase();
    return text.includes(q);
  });


  const teamRunsRows = teamRunsFiltered;

  // Planners
  const plannersFiltered = plannersData.filter((planner) => {
    const q = term(plannersSearch);
    if (!q) return true;
    const team = TEAMS_DATA.find((t) => t.team_id === planner.team_id);

    // Handle both old and new API structures
    const pipelineNamesStr = planner.pipeline_names ? planner.pipeline_names.join(' ') : '';
    const agentNamesStr = planner.agent_names ? planner.agent_names.join(' ') : '';

    const text = [
      planner.planner_name,
      planner.name, // New API field
      planner.description, // New API field
      team ? team.team_name : '',
      pipelineNamesStr,
      agentNamesStr,
      planner.core_module_mapping,
      planner.llm_model,
      planner.status,
      planner.last_active
    ]
      .join(' ')
      .toLowerCase();
    return text.includes(q);
  });
  const plannersTotalPages = Math.max(1, Math.ceil(plannersFiltered.length / PAGE_SIZE));
  const plannersCurrentPage = Math.min(plannersPage, plannersTotalPages);
  const plannersStart = (plannersCurrentPage - 1) * PAGE_SIZE;
  const plannersRowsBase = plannersFiltered.slice(plannersStart, plannersStart + PAGE_SIZE);

  const plannerSuccessRule =
    (thresholdConfig.entities && thresholdConfig.entities.planners && thresholdConfig.entities.planners.success_rate_pct) ||
    DEFAULT_THRESHOLD_CONFIG.entities.planners.success_rate_pct;

  const plannersRows = plannersRowsBase.map((planner) => {
    // Check if this is the new API format with config object
    if (planner.config) {
      // New API format: extract config and merge with aggregate metrics
      const { config, total_runs, total_tokens, success_rate, average_execution_time, agent_names, pipeline_names } = planner;
      const successRate = success_rate || 0;
      const level = successRate != null ? evaluateThresholdLevel(successRate, plannerSuccessRule) : null;
      const effective_status = level || (total_runs > 0 ? 'active' : 'inactive');

      return {
        ...config,
        planner_id: config.code,
        planner_name: config.name || config.displayName,
        description: config.description,
        total_runs,
        total_tokens,
        success_rate: successRate,
        average_execution_time,
        agent_names,
        pipeline_names,
        effective_status,
        status: effective_status
      };
    } else {
      // Old format: use session-based calculation
      const runs = plannerRunsData.filter((run) => run.planner_id === planner.planner_id);
      const successRate = computeSuccessRate(runs);
      const level = successRate != null ? evaluateThresholdLevel(successRate, plannerSuccessRule) : null;
      const effective_status = level || planner.status;
      return { ...planner, effective_status };
    }
  });

  // Planner Runs (API-driven)
  const plannerRunsSource = plannerRunsApiData.length ? plannerRunsApiData : plannerRunsData;
  const plannerRunsFiltered = plannerRunsSource.filter((run) => {
    const q = term(plannerRunsSearch);
    if (!q) return true;

    // Handle new API structure with config object
    const runData = run.config ? {
      planner_id: run.config.code,
      planner_run_id: run.config.code,
      run_id: run.config.code,
      metric_type_name: run.config.name,
      status: run.total_runs > 0 ? 'Active' : 'Inactive',
      started_at: run.config.created_at,
      ...run
    } : run;

    const planner = PLANNERS_DATA.find((p) => p.planner_id === runData.planner_id);
    const teamRun = TEAM_RUNS_DATA.find((tr) => tr.team_run_id === runData.team_run_id);
    const team = teamRun ? TEAMS_DATA.find((t) => t.team_id === teamRun.team_id) : null;
    const parent = runData.parent_run_context || {};
    const plannerName = runData.metric_type_name || (planner ? planner.planner_name : '');
    const pipelineNamesStr = run.pipeline_names ? run.pipeline_names.join(' ') : '';
    const agentNamesStr = run.agent_names ? run.agent_names.join(' ') : '';
    const teamName =
      parent && parent.entity_type === 'pipeline' && parent.entity_name
        ? parent.entity_name
        : team
          ? team.team_name
          : '';
    const llmModel = (runData.run_data && runData.run_data.model) || runData.llm_model || '';

    const text = [
      runData.planner_run_id || runData.run_id,
      plannerName,
      pipelineNamesStr,
      agentNamesStr,
      teamName,
      runData.status,
      runData.started_at,
      llmModel
    ]
      .join(' ')
      .toLowerCase();
    return text.includes(q);
  });

  const plannerRunsRows = plannerRunsFiltered;

  // Agents
  const agentsFiltered = agentsData.filter((agent) => {
    const q = term(agentsSearch);
    if (!q) return true;
    const planner = PLANNERS_DATA.find((p) => p.planner_id === agent.planner_id);
    const team = TEAMS_DATA.find((t) => t.team_id === agent.team_id);

    // Handle both old and new API structures
    const plannerNamesStr = agent.planner_names ? agent.planner_names.join(' ') : '';
    const pipelineNamesStr = agent.pipeline_names ? agent.pipeline_names.join(' ') : '';

    const text = [
      agent.agent_name,
      agent.name, // New API field
      agent.description, // New API field
      planner ? planner.planner_name : '',
      plannerNamesStr,
      team ? team.team_name : '',
      agent.core_module_mapping,
      agent.role,
      agent.llm_model,
      agent.status,
      agent.last_active,
      pipelineNamesStr
    ]
      .join(' ')
      .toLowerCase();
    return text.includes(q);
  });
  const agentsTotalPages = Math.max(1, Math.ceil(agentsFiltered.length / PAGE_SIZE));
  const agentsCurrentPage = Math.min(agentsPage, agentsTotalPages);
  const agentsStart = (agentsCurrentPage - 1) * PAGE_SIZE;
  const agentsRowsBase = agentsFiltered.slice(agentsStart, agentsStart + PAGE_SIZE);

  const agentSuccessRule =
    (thresholdConfig.entities && thresholdConfig.entities.agents && thresholdConfig.entities.agents.success_rate_pct) ||
    DEFAULT_THRESHOLD_CONFIG.entities.agents.success_rate_pct;

  const agentsRows = agentsRowsBase.map((agent) => {
    // Check if this is the new API format with config object
    if (agent.config) {
      // New API format: extract config and merge with aggregate metrics
      const { config, total_runs, total_tokens, success_rate, average_execution_time, pipeline_names, planner_names } = agent;
      const successRate = success_rate || 0;
      const level = successRate != null ? evaluateThresholdLevel(successRate, agentSuccessRule) : null;
      const effective_status = level || (total_runs > 0 ? 'active' : 'inactive');

      return {
        ...config,
        agent_id: config.code,
        agent_name: config.name || config.displayName,
        description: config.description,
        total_runs,
        total_tokens,
        success_rate: successRate,
        average_execution_time,
        pipeline_names,
        planner_names,
        effective_status,
        status: effective_status
      };
    } else {
      // Old format: use session-based calculation
      const sessions = agentSessionsData.filter((session) => session.agent_id === agent.agent_id);
      const successRate = computeSuccessRate(sessions);
      const level = successRate != null ? evaluateThresholdLevel(successRate, agentSuccessRule) : null;
      const effective_status = level || agent.status;
      return { ...agent, effective_status };
    }
  });

  // Agent Runs (API-driven)
  const agentRunsSource = agentRunsApiData.length ? agentRunsApiData : agentSessionsData;
  // console.log("Agent run source : ", agentRunsSource)
  const agentRunsFiltered = agentRunsSource.filter((session) => {
    const q = term(agentRunsSearch);
    if (!q) return true;

    // Handle new API structure with config object
    const sessionData = session.config ? {
      agent_id: session.config.code,
      agent_session_id: session.config.code,
      run_id: session.config.code,
      metric_type_name: session.config.name,
      status: session.total_runs > 0 ? 'Active' : 'Inactive',
      started_at: session.config.created_at,
      ...session
    } : session;

    const agent = AGENTS_DATA.find((a) => a.agent_id === sessionData.agent_id);
    const planner = PLANNERS_DATA.find((p) => p.planner_id === sessionData.planner_id);
    const team = TEAMS_DATA.find((t) => t.team_id === sessionData.team_id);
    const parent = sessionData.parent_run_context || {};
    const agentName = sessionData.metric_type_name || (agent ? agent.agent_name : '');
    const pipelineNamesStr = session.pipeline_names ? session.pipeline_names.join(' ') : '';
    const plannerNamesStr = session.planner_names ? session.planner_names.join(' ') : '';
    const teamName =
      parent && parent.entity_type === 'pipeline' && parent.entity_name
        ? parent.entity_name
        : team
          ? team.team_name
          : '';
    const llmModel = (sessionData.run_data && sessionData.run_data.model) || sessionData.llm_model || '';
    const text = [
      sessionData.agent_session_id || sessionData.run_id,
      agentName,
      pipelineNamesStr,
      plannerNamesStr,
      teamName,
      sessionData.status,
      sessionData.started_at,
      llmModel
    ]
      .join(' ')
      .toLowerCase();
    return text.includes(q);
  });

  const agentRunsRows = agentRunsFiltered;

  // --- Helper: format tool-call timestamp into a short readable time ---
  const formatToolCallTime = (createdAt) => {
    if (!createdAt) return '—';
    // Treat createdAt as a Unix timestamp in seconds for this demo
    const date = new Date(createdAt * 1000);
    if (Number.isNaN(date.getTime())) return String(createdAt);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    const ss = String(date.getSeconds()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
  };

  const formatYesNo = (value) => {
    if (value === null || typeof value === 'undefined') return '—';
    return value ? 'Yes' : 'No';
  };

  // Format a duration in milliseconds into a human-readable string.
  // 1) >= 24 hours -> "Xd Yh"
  // 2) >= 60 minutes -> "Xh" (hours)
  // 3) >= 1 second -> "Xs" (seconds)
  // 4) < 1 second  -> "Xms"
  const formatDurationMs = (ms) => {
    if (ms == null || Number.isNaN(ms)) return 'n/a';

    const sec = ms / 1000;
    const min = sec / 60;
    const hr = min / 60;
    const day = hr / 24;

    if (day >= 1) {
      const days = Math.floor(day);
      const remainingHours = Math.floor(hr - days * 24);
      return `${days}d ${remainingHours}h`;
    }
    if (hr >= 1) {
      const hoursRounded = Math.round(hr * 10) / 10;
      return `${hoursRounded}h`;
    }
    if (min >= 1) {
      const minutesRounded = Math.round(min * 10) / 10;
      return `${minutesRounded}min`;
    }
    const msRounded = Math.round(ms);
    if (sec >= 1) {
      const secondsRounded = Math.round(sec * 10) / 10;
      return `${secondsRounded}s`;
    }
    return `${msRounded}ms`;
  };

  return (
    <PageContainer>
      <motion.div initial="hidden" animate="visible" variants={fadeIn}>
        {/* <PageHeaderWithInfo
          icon={faWalkieTalkie}
          title="Observability"
          description="End-to-end observability of teams, planners, agents and runs, aligned with the Observability theme."
        >
          <ActionBar style={{ marginTop: 0, marginBottom: 0 }}>
            <Button>Export View</Button>
            <Button primary onClick={() => setThresholdsOpen(true)}>
              Configure Thresholds
            </Button>
          </ActionBar>
        </PageHeaderWithInfo> */}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
          <div style={{ position: 'relative' }} ref={datePickerRef}>
            <button
              onClick={() => setShowDatePicker((v) => !v)}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 8,
                background: '#ffffff',
                cursor: 'pointer',
                minWidth: 220,
                textAlign: 'left'
              }}
            >
              {rangeLabel}
            </button>
            {showDatePicker && (
              <div style={{ position: 'absolute', right: 0, zIndex: 30 }}>
                <DateRangePicker
                  ranges={draftRange}
                  onChange={(item) => setDraftRange([item.selection])}
                  months={1}
                  direction="horizontal"
                  rangeColors={["#0ea5e9"]}
                  showDateDisplay={true}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, background: '#ffffff', border: '1px solid #e5e7eb', borderTop: 'none', padding: '8px 12px', borderRadius: '0 0 8px 8px' }}>
                  <button
                    onClick={() => { setShowDatePicker(false); setDraftRange(appliedRange); }}
                    style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, background: '#ffffff', cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setAppliedRange(draftRange);
                      setShowDatePicker(false);
                    }}
                    style={{ padding: '6px 10px', border: '1px solid #0ea5e9', color: '#0ea5e9', borderRadius: 6, background: '#ffffff', cursor: 'pointer' }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <SummaryCards dateFilter={dateFilterMs} />

          {/* Performance Cards Grid */}
          <PerformanceCardsGrid>
            {/* Agents Performance Card */}
            <PerformanceCard
              title="Agents"
              graphData={{
                ...agentGraphData,
                totalLabel: "Total Sessions",
                failedLabel: "Failed Sessions"
              }}
              isLoading={(agentPerfTab === 'agents' ? agentsLoading : agentRunsLoading) || agentGraphLoading}
              performanceData={
                agentPerfTab === 'agents'
                  ? agentsRows.map((agent) => {
                    // Use aggregate success_rate if available (new API), otherwise calculate from sessions
                    const successRate = agent.success_rate != null
                      ? agent.success_rate
                      : (() => {
                        const sessions = agentSessionsData.filter((s) => s.agent_id === agent.agent_id);
                        return computeSuccessRate(sessions);
                      })();
                      console.log(agent,"agent----------------------------")
                    const formatTokens = (tokens) => {
                      if (!tokens) return 'N/A';
                      if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
                      if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
                      return tokens.toString();
                    };

                    return {
                      id: agent.id,
                      agent_id: agent.agent_id,
                      name: agent.agent_name || agent.name,
                      subtext: agent.description || agent.role || agent.core_module_mapping,
                      metric: successRate != null ? `${Math.round(successRate)}%` : 'N/A',
                      metricStatus: successRate >= 95 ? 'Healthy' : successRate >= 80 ? 'Warning' : 'Inactive',
                      status: formatTokens(agent.total_tokens),
                      total_tokens: agent.total_tokens
                    };
                  })
                  : agentRunsRows.map((run) => {
                    const formatTokens = (tokens) => {
                      if (!tokens) return 'N/A';
                      if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
                      if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
                      return tokens.toString();
                    };

                    const successRate = run.success_rate || 0;
                    const agent = AGENTS_DATA.find((a) => a.agent_id === run.agent_id);
                    console.log(run,"------------------------RUN")
                    return {
                      id: run.id,
                      name: run.run_id || run.id,
                      subtext: agent ? agent.agent_name : run.metric_type_name || '',
                      metric: successRate != null ? `${Math.round(successRate)}%` : 'N/A',
                      metricStatus: successRate >= 95 ? 'Healthy' : successRate >= 80 ? 'Warning' : 'Inactive',
                      status: formatTokens(run.total_tokens),
                      total_tokens: run.total_tokens
                    };
                  })
              }
              tabs={[
                { key: 'agents', label: 'Agents' },
                { key: 'agent-sessions', label: 'Agents Sessions' }
              ]}
              activeTab={agentPerfTab}
              onTabChange={setAgentPerfTab}
              onViewAll={() => navigate('/observability/details?tab=' + (agentPerfTab === 'agents' ? 'agents' : 'agent-runs') + `&start=${dateFilterMs?.started_at?.$gte}&end=${dateFilterMs?.started_at?.$lte}`)}
              type="agents"
            />

            {/* Teams Performance Card */}
            <PerformanceCard
              title="Teams"
              graphData={{
                ...teamGraphData,
                totalLabel: "Total Runs",
                failedLabel: "Failed Runs"
              }}
              isLoading={(teamPerfTab === 'teams' ? teamsLoading : teamRunsLoading) || teamGraphLoading}
              performanceData={
                teamPerfTab === 'teams'
                  ? teamsRows.map((team) => {
                    const successRate = team.success_rate != null ? team.success_rate : 0;
                    console.log("------------------TEAMS", team)
                    const formatTokens = (tokens) => {
                      if (!tokens) return 'N/A';
                      if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
                      if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
                      return tokens.toString();
                    };

                    return {
                      id: team.id,
                      team_id: team.team_id || team.code,
                      name: team.team_name || team.pipeline_name,
                      subtext: team.description || team.domain_context,
                      metric: successRate != null ? `${Math.round(successRate)}%` : 'N/A',
                      metricStatus: successRate >= 95 ? 'Healthy' : successRate >= 80 ? 'Warning' : 'Inactive',
                      status: formatTokens(team.total_tokens),
                      total_tokens: team.total_tokens
                    };
                  })
                  : teamRunsRows.map((run) => {
                    const formatTokens = (tokens) => {
                      if (!tokens) return 'N/A';
                      if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
                      if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
                      return tokens.toString();
                    };
                    console.log("------------------TEAMS RUN", run)

                    const successRate = run.success_rate || 0;
                    const team = TEAMS_DATA.find((t) => t.team_id === run.team_id);
                    const pipelineName = run.metric_type_name || (team ? team.pipeline_name || team.team_name : '');
                    
                    return {
                      id: run.id,
                      name: run.run_id || run.team_run_id || run.id,
                      subtext: pipelineName,
                      metric: successRate != null ? `${Math.round(successRate)}%` : 'N/A',
                      metricStatus: successRate >= 95 ? 'Healthy' : successRate >= 80 ? 'Warning' : 'Inactive',
                      status: formatTokens(run.total_tokens),
                      total_tokens: run.total_tokens
                    };
                  })
              }
              tabs={[
                { key: 'teams', label: 'Teams' },
                { key: 'teams-run', label: 'Teams Run' }
              ]}
              activeTab={teamPerfTab}
              onTabChange={setTeamPerfTab}
              onViewAll={() => navigate('/observability/details?tab=' + (teamPerfTab === 'teams' ? 'teams' : 'team-runs') + `&start=${dateFilterMs?.started_at?.$gte}&end=${dateFilterMs?.started_at?.$lte}`)}
              type="teams"
            />

            {/* Planners Performance Card */}
            <PerformanceCard
              title="Planner"
              graphData={{
                ...plannerGraphData,
                totalLabel: "Total Runs",
                failedLabel: "Failed Runs"
              }}
              isLoading={(plannerPerfTab === 'planners' ? plannersLoading : plannerRunsLoading) || plannerGraphLoading}
              performanceData={
                plannerPerfTab === 'planners'
                  ? plannersRows.map((planner) => {
                    const successRate = planner.success_rate != null ? planner.success_rate : 0;
                    console.log("------------------PLANNER", planner)
                    
                    const formatTokens = (tokens) => {
                      if (!tokens) return 'N/A';
                      if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
                      if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
                      return tokens.toString();
                    };

                    return {
                      id: planner.id,
                      planner_id: planner.planner_id || planner.code,
                      name: planner.planner_name || planner.name,
                      subtext: planner.description || planner.core_module_mapping,
                      metric: successRate != null ? `${Math.round(successRate)}%` : 'N/A',
                      metricStatus: successRate >= 95 ? 'Healthy' : successRate >= 80 ? 'Warning' : 'Inactive',
                      status: formatTokens(planner.total_tokens),
                      total_tokens: planner.total_tokens
                    };
                  })
                  : plannerRunsRows.map((run) => {
                    const formatTokens = (tokens) => {
                      if (!tokens) return 'N/A';
                      if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
                      if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
                      return tokens.toString();
                    };
                    console.log("------------------PLANNER RUN", run)

                    const successRate = run.success_rate || 0;
                    const planner = PLANNERS_DATA.find((p) => p.planner_id === run.planner_id);
                    const plannerName = run.metric_type_name || (planner ? planner.planner_name : '');
                    
                    return {
                      id: run.id,
                      name: run.run_id || run.planner_run_id || run.id,
                      subtext: plannerName,
                      metric: successRate != null ? `${Math.round(successRate)}%` : 'N/A',
                      metricStatus: successRate >= 95 ? 'Healthy' : successRate >= 80 ? 'Warning' : 'Inactive',
                      status: formatTokens(run.total_tokens),
                      total_tokens: run.total_tokens
                    };
                  })
              }
              tabs={[
                { key: 'planners', label: 'Planner' },
                { key: 'planner-run', label: 'Planner Run' }
              ]}
              activeTab={plannerPerfTab}
              onTabChange={setPlannerPerfTab}
              onViewAll={() => navigate('/observability/details?tab=' + (plannerPerfTab === 'planners' ? 'planners' : 'planner-runs') + `&start=${dateFilterMs?.started_at?.$gte}&end=${dateFilterMs?.started_at?.$lte}`)}
              type="planners"
            />
          </PerformanceCardsGrid>

          {/* System section: System Health & Alerts - COMMENTED OUT */}
          {/* <Card style={{ marginTop: '1.5rem' }}>
          <CardTitle>System</CardTitle>
          <CardContent>
            <TabContainer>
              <TabList>
                <Tab
                  active={systemTab === 'health'}
                  onClick={() => setSystemTab('health')}
                  style={{ fontWeight: 600 }}
                >
                  System Health
                </Tab>
                <Tab
                  active={systemTab === 'alerts'}
                  onClick={() => setSystemTab('alerts')}
                  style={{ fontWeight: 600 }}
                >
                  Alerts
                </Tab>
              </TabList>

              <TabContent active={systemTab === 'health'}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.75rem',
                    gap: '0.75rem',
                    flexWrap: 'wrap'
                  }}
                >
                  <SearchInput
                    type="text"
                    placeholder="Search system health..."
                    value={systemHealthSearch}
                    onChange={(e) => {
                      setSystemHealthSearch(e.target.value);
                      setSystemHealthPage(1);
                    }}
                    style={{ maxWidth: '260px' }}
                  />
                </div>
                <Table>
                  <thead>
                    <tr>
                      <TableHeader>ID</TableHeader>
                      <TableHeader>Service</TableHeader>
                      <TableHeader>CPU (%)</TableHeader>
                      <TableHeader>Memory (%)</TableHeader>
                      <TableHeader>Latency (ms)</TableHeader>
                      <TableHeader>Throughput (req/min)</TableHeader>
                      <TableHeader>Error Rate (%)</TableHeader>
                      <TableHeader>Uptime (%)</TableHeader>
                      <TableHeader>Status</TableHeader>
                      <TableHeader>Actions</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {systemHealthRowsWithThresholds.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{row.service}</TableCell>
                        <TableCell>{row.cpu_pct}%</TableCell>
                        <TableCell>{row.memory_pct}%</TableCell>
                        <TableCell>{row.latency_ms} ms</TableCell>
                        <TableCell>{row.throughput_rpm} req/min</TableCell>
                        <TableCell>{row.error_rate_pct}%</TableCell>
                        <TableCell>{row.uptime_pct}%</TableCell>
                        <TableCell>
                          <StatusBadge status={getStatusBadgeStatus(row.effective_status)}>
                            {row.effective_status}
                          </StatusBadge>
                        </TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <Button
                              style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}
                              onClick={() =>
                                openDetailsModal('System – ' + row.service, [
                                  { label: 'ID', value: row.id },
                                  { label: 'Service', value: row.service },
                                  { label: 'CPU', value: `${row.cpu_pct}%` },
                                  { label: 'Memory', value: `${row.memory_pct}%` },
                                  { label: 'Latency', value: `${row.latency_ms} ms` },
                                  { label: 'Throughput', value: `${row.throughput_rpm} req/min` },
                                  { label: 'Error Rate', value: `${row.error_rate_pct}%` },
                                  { label: 'Uptime', value: `${row.uptime_pct}%` },
                                  { label: 'Status', value: row.status }
                                ])
                              }
                            >
                              Details
                            </Button>
                            <Button
                              style={{
                                padding: '0.3rem 0.6rem',
                                fontSize: '0.8rem',
                                backgroundColor: 'transparent',
                                color: '#FF3B30',
                                border: '1px solid #FF3B30'
                              }}
                              title="Restart"
                            >
                              <FontAwesomeIcon icon={faRedo} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </TabContent>

              <TabContent active={systemTab === 'alerts'}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.75rem',
                    gap: '0.75rem',
                    flexWrap: 'wrap'
                  }}
                >
                  <SearchInput
                    type="text"
                    placeholder="Search alerts..."
                    value={alertsSearch}
                    onChange={(e) => {
                      setAlertsSearch(e.target.value);
                      setAlertsPage(1);
                    }}
                    style={{ maxWidth: '260px' }}
                  />
                </div>
                <Table>
                  <thead>
                    <tr>
                      <TableHeader>ID</TableHeader>
                      <TableHeader>Timestamp</TableHeader>
                      <TableHeader>Service</TableHeader>
                      <TableHeader>Message</TableHeader>
                      <TableHeader>Severity</TableHeader>
                      <TableHeader>Status</TableHeader>
                      <TableHeader>Actions</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {alertsRows.map((alert) => (
                      <TableRow key={alert.id}>
                        <TableCell>{alert.id}</TableCell>
                        <TableCell>{alert.timestamp}</TableCell>
                        <TableCell>{alert.service}</TableCell>
                        <TableCell>{alert.message}</TableCell>
                        <TableCell>
                          <StatusBadge status={getSeverityBadgeStatus(alert.severity)}>
                            {alert.severity}
                          </StatusBadge>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={getStatusBadgeStatus(alert.status)}>
                            {alert.status}
                          </StatusBadge>
                        </TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <Button
                              style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}
                              onClick={() =>
                                openDetailsModal('Alert – ' + alert.id, [
                                  { label: 'ID', value: alert.id },
                                  { label: 'Timestamp', value: alert.timestamp },
                                  { label: 'Service', value: alert.service },
                                  { label: 'Message', value: alert.message },
                                  { label: 'Severity', value: alert.severity },
                                  { label: 'Status', value: alert.status }
                                ])
                              }
                            >
                              Details
                            </Button>
                            {alert.status !== 'Resolved' && (
                              <Button
                                primary
                                style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}
                              >
                                Resolve
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </TabContent>
            </TabContainer>
          </CardContent>
        </Card> */}

          {/* Teams section: Teams & Team Runs */}
          {/* <Card style={{ marginTop: '1.5rem' }}>
          <CardTitle>Teams</CardTitle>
          <CardContent>
            <TabContainer>
              <TabList>
                <Tab
                  active={teamsTab === 'teams'}
                  onClick={() => setTeamsTab('teams')}
                  style={{ fontWeight: 600 }}
                >
                  Teams
                </Tab>
                <Tab
                  active={teamsTab === 'team-runs'}
                  onClick={() => setTeamsTab('team-runs')}
                  style={{ fontWeight: 600 }}
                >
                  Team Runs
                </Tab>
              </TabList>

              <TabContent active={teamsTab === 'teams'}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.75rem',
                    gap: '0.75rem',
                    flexWrap: 'wrap'
                  }}
                >
                  <SearchInput
                    type="text"
                    placeholder="Search teams..."
                    value={teamsSearch}
                    onChange={(e) => {
                      setTeamsSearch(e.target.value);
                      setTeamsPage(1);
                    }}
                    style={{ maxWidth: '260px' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    
                  </div>
                </div>
                <Table>
                  <thead>
                    <tr>
                      <TableHeader>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                          <Network size={14} />
                          <span>Team</span>
                        </span>
                      </TableHeader>
                      <TableHeader>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                          <Workflow size={14} />
                          <span>Planner</span>
                        </span>
                      </TableHeader>
                      <TableHeader>Domain / Module</TableHeader>
                      <TableHeader>Status</TableHeader>
                      <TableHeader>Last Active</TableHeader>
                      <TableHeader>Actions</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {teamsRows.map((team) => {
                      const planner = PLANNERS_DATA.find((p) => p.planner_id === team.planner_id);
                      return (
                        <TableRow key={team.team_id}>
                          <TableCell>{team.team_name}</TableCell>
                          <TableCell>{planner ? planner.planner_name : ''}</TableCell>
                          <TableCell>{team.domain_context}</TableCell>
                          <TableCell>
                            <StatusBadge status={getStatusBadgeStatus(team.effective_status)}>
                              {team.effective_status}
                            </StatusBadge>
                          </TableCell>
                          <TableCell>{team.last_active}</TableCell>
                          <TableCell>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <Button
                                style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}
                                onClick={() =>
                                  openDetailsModal('Team – ' + team.team_name, [
                                    { label: 'Team ID', value: team.team_id },
                                    { label: 'Team', value: team.team_name },
                                    { label: 'Planner', value: planner ? planner.planner_name : '' },
                                    { label: 'Pipeline', value: team.pipeline_name },
                                    { label: 'Domain / Module', value: team.domain_context },
                                    { label: 'Status', value: team.status },
                                    { label: 'Last Active', value: team.last_active }
                                  ])
                                }
                              >
                                Details
                              </Button>
                              {String(team.status).toLowerCase() === 'active' && (
                                <Button
                                  style={{
                                    padding: '0.3rem 0.6rem',
                                    fontSize: '0.8rem',
                                    backgroundColor: 'transparent',
                                    color: '#FF3B30',
                                    border: '1px solid #FF3B30'
                                  }}
                                  onClick={() => deactivateTeam(team.team_id)}
                                >
                                  Deactivate
                                </Button>
                              )}
                              {String(team.status).toLowerCase() === 'inactive' && (
                                <Button
                                  style={{
                                    padding: '0.3rem 0.6rem',
                                    fontSize: '0.8rem',
                                    backgroundColor: 'transparent',
                                    color: '#22C55E',
                                    border: '1px solid #22C55E'
                                  }}
                                  onClick={() => activateTeam(team.team_id)}
                                >
                                  Activate
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </tbody>
                </Table>
                <Pagination
                  currentPage={teamPagination?.currentPage}
                  totalPages={teamPagination?.totalPages}
                  totalCount={teamPagination?.totalItems}
                  setCurrentPage={teamPagination?.setCurrentPage}
                  setTotalPages={teamPagination?.setTotalPages}
                />
              </TabContent>

              <TabContent active={teamsTab === 'team-runs'}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.75rem',
                    gap: '0.75rem',
                    flexWrap: 'wrap'
                  }}
                >
                  <SearchInput
                    type="text"
                    placeholder="Search team runs..."
                    value={teamRunsSearch}
                    onChange={handleTeamRunsSearchChange}
                    style={{ maxWidth: '260px' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
             
                  </div>
                </div>
                <Table>
                  <thead>
                    <tr>
                      <TableHeader>Run ID</TableHeader>
                      <TableHeader>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                          <Network size={14} />
                          <span>Pipeline</span>
                        </span>
                      </TableHeader>
                      <TableHeader>Status</TableHeader>
                      <TableHeader>Started At</TableHeader>
                      <TableHeader>Duration</TableHeader>
                      <TableHeader>Total Tokens</TableHeader>
                      <TableHeader>Actions</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {teamRunsLoading && (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.5rem 0' }}>
                            <InlineSpinner />
                            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Loading team runs...</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    {teamRunsError && !teamRunsLoading && (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <span style={{ fontSize: '0.85rem', color: '#FF3B30' }}>{teamRunsError}</span>
                        </TableCell>
                      </TableRow>
                    )}
                    {!teamRunsLoading && !teamRunsError && teamRunsRows.map((run) => {
                      const team = TEAMS_DATA.find((t) => t.team_id === run.team_id);
                      const id = run.team_run_id || run.run_id;
                      const recordId = run.metric_id || run.id;
                      const pipelineName = run.metric_type_name || (team ? team.pipeline_name || team.team_name : '');
                      return (
                        <>
                        <TableRow
                          key={id}
                          style={{ cursor: recordId ? 'pointer' : 'default' }}
                          onClick={() => recordId && navigate(`/observability/details/${recordId}`)}
                        >
                          <TableCell>{id}</TableCell>
                          <TableCell>{pipelineName}</TableCell>
                          <TableCell>
                            <StatusBadge status={getStatusBadgeStatus(run.status)}>
                              {run.status}
                            </StatusBadge>
                          </TableCell>
                          <TableCell>{formatRelativeTime(run.started_at)}</TableCell>
                          <TableCell>
                            {run.duration_ms != null
                              ? formatDurationMs(run.duration_ms)
                              : <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>n/a</span>}
                          </TableCell>
                          <TableCell>{run.total_tokens}</TableCell>
                          <TableCell>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <Button
                                style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDetailsModal('Pipeline Run – ' + id, [
                                    { label: 'Run ID', value: id },
                                    { label: 'Pipeline', value: pipelineName },
                                    { label: 'Status', value: run.status },
                                    { label: 'Started At', value: formatRelativeTime(run.started_at) },
                                    { label: 'Finished At', value: run.finished_at || 'n/a' },
                                    { label: 'Duration (ms)', value: run.duration_ms != null ? run.duration_ms : 'n/a' },
                                    { label: 'Total Tokens', value: run.total_tokens },
                                    { label: 'LLM Model', value: run.llm_model }
                                  ]);
                                }}
                              >
                                Details
                              </Button>
                              {['success', 'failed'].includes(String(run.status).toLowerCase()) && (
                                <Button
                                  style={{
                                    padding: '0.3rem 0.6rem',
                                    fontSize: '0.8rem',
                                    backgroundColor: 'transparent',
                                    color: '#FF3B30',
                                    border: '1px solid #FF3B30'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    retryTeamRun(run.team_run_id);
                                  }}
                                  title="Retry"
                                >
                                  <FontAwesomeIcon icon={faRedo} />
                                </Button>
                              )}
                              {String(run.status).toLowerCase() === 'running' && (
                                <Button
                                  style={{
                                    padding: '0.3rem 0.6rem',
                                    fontSize: '0.8rem',
                                    backgroundColor: 'transparent',
                                    color: '#FF3B30',
                                    border: '1px solid #FF3B30'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    pauseTeamRun(run.team_run_id);
                                  }}
                                  title="Pause"
                                >
                                  <FontAwesomeIcon icon={faPause} />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                        </>
                      );
                    })}
                  </tbody>
                </Table>
                <Pagination
                  currentPage={teamRunPagination?.currentPage}
                  totalPages={teamRunPagination?.totalPages}
                  totalCount={teamRunPagination?.totalItems}
                  setCurrentPage={teamRunPagination?.setCurrentPage}
                  setTotalPages={teamRunPagination?.setTotalPages}
                />
              </TabContent>
            </TabContainer>
          </CardContent>
        </Card> */}

          {/* Planners section: Planners & Planner Runs */}
          {/* <Card style={{ marginTop: '1.5rem' }}>
          <CardTitle>Planners</CardTitle>
          <CardContent>
            <TabContainer>
              <TabList>
                <Tab
                  active={plannersTab === 'planners'}
                  onClick={() => setPlannersTab('planners')}
                  style={{ fontWeight: 600 }}
                >
                  Planners
                </Tab>
                <Tab
                  active={plannersTab === 'planner-runs'}
                  onClick={() => setPlannersTab('planner-runs')}
                  style={{ fontWeight: 600 }}
                >
                  Planner Runs
                </Tab>
              </TabList>

              <TabContent active={plannersTab === 'planners'}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.75rem',
                    gap: '0.75rem',
                    flexWrap: 'wrap'
                  }}
                >
                  <SearchInput
                    type="text"
                    placeholder="Search planners..."
                    value={plannersSearch}
                    onChange={(e) => {
                      setPlannersSearch(e.target.value);
                      setPlannersPage(1);
                    }}
                    style={{ maxWidth: '260px' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                 
                  </div>
                </div>
                <Table>
                  <thead>
                    <tr>
                      <TableHeader>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                          <Workflow size={14} />
                          <span>Planner</span>
                        </span>
                      </TableHeader>
                      <TableHeader>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                          <Network size={14} />
                          <span>Team</span>
                        </span>
                      </TableHeader>
                      <TableHeader>Core Module Mapping</TableHeader>
                      <TableHeader>LLM Model</TableHeader>
                      <TableHeader>Status</TableHeader>
                      <TableHeader>Last Active</TableHeader>
                      <TableHeader>Actions</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {plannersRows.map((planner) => {
                      const team = TEAMS_DATA.find((t) => t.team_id === planner.team_id);
                      const agentsForPlanner = AGENTS_DATA.filter((a) => a.planner_id === planner.planner_id);
                      const agentsLabel = agentsForPlanner.length
                        ? agentsForPlanner.map((a) => a.agent_name || a.agent_id).join(', ')
                        : '—';
                      return (
                        <TableRow key={planner.planner_id}>
                          <TableCell>{planner.planner_name}</TableCell>
                          <TableCell>{team ? team.team_name : ''}</TableCell>
                          <TableCell>{planner.core_module_mapping}</TableCell>
                          <TableCell>
                            <span
                              style={{
                                display: 'inline-flex',
                                padding: '2px 8px',
                                borderRadius: '999px',
                                border: '1px solid #e2e8f0',
                                fontSize: '0.75rem'
                              }}
                            >
                              {planner.llm_model}
                            </span>
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={getStatusBadgeStatus(planner.effective_status)}>
                              {planner.effective_status}
                            </StatusBadge>
                          </TableCell>
                          <TableCell>{planner.last_active}</TableCell>
                          <TableCell>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <Button
                                style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}
                                onClick={() =>
                                  openDetailsModal('Planner – ' + planner.planner_name, [
                                    { label: 'Planner ID', value: planner.planner_id },
                                    { label: 'Planner', value: planner.planner_name },
                                    { label: 'Team', value: team ? team.team_name : '' },
                                    { label: 'Core Module Mapping', value: planner.core_module_mapping },
                                    { label: 'LLM Model', value: planner.llm_model },
                                    { label: 'Agents', value: agentsLabel },
                                    { label: 'Status', value: planner.status },
                                    { label: 'Last Active', value: planner.last_active }
                                  ])
                                }
                              >
                                Details
                              </Button>
                              {String(planner.status).toLowerCase() === 'active' && (
                                <Button
                                  style={{
                                    padding: '0.3rem 0.6rem',
                                    fontSize: '0.8rem',
                                    backgroundColor: 'transparent',
                                    color: '#FF3B30',
                                    border: '1px solid #FF3B30'
                                  }}
                                  onClick={() => deactivatePlanner(planner.planner_id)}
                                >
                                  Deactivate
                                </Button>
                              )}
                              {String(planner.status).toLowerCase() === 'inactive' && (
                                <Button
                                  style={{
                                    padding: '0.3rem 0.6rem',
                                    fontSize: '0.8rem',
                                    backgroundColor: 'transparent',
                                    color: '#22C55E',
                                    border: '1px solid #22C55E'
                                  }}
                                  onClick={() => activatePlanner(planner.planner_id)}
                                >
                                  Activate
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </tbody>
                </Table>
              </TabContent>

              <TabContent active={plannersTab === 'planner-runs'}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.75rem',
                    gap: '0.75rem',
                    flexWrap: 'wrap'
                  }}
                >
                  <SearchInput
                    type="text"
                    placeholder="Search planner runs..."
                    value={plannerRunsSearch}
                    onChange={handlePlannerRunsSearchChange}
                    style={{ maxWidth: '260px' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        
                  </div>
                </div>
                <Table>
                  <thead>
                    <tr>
                      <TableHeader>Planner Run ID</TableHeader>
                      <TableHeader>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                          <Workflow size={14} />
                          <span>Planner</span>
                        </span>
                      </TableHeader>
                      <TableHeader>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                          <Network size={14} />
                          <span>Team</span>
                        </span>
                      </TableHeader>
                      <TableHeader>Status</TableHeader>
                      <TableHeader>Started At</TableHeader>
                      <TableHeader>Duration (ms)</TableHeader>
                      <TableHeader>Total Tokens</TableHeader>
                      <TableHeader>LLM Model</TableHeader>
                      <TableHeader>Actions</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {plannerRunsLoading && (
                      <TableRow>
                        <TableCell colSpan={9}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.5rem 0' }}>
                            <InlineSpinner />
                            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Loading planner runs...</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    {plannerRunsError && !plannerRunsLoading && (
                      <TableRow>
                        <TableCell colSpan={9}>
                          <span style={{ fontSize: '0.85rem', color: '#FF3B30' }}>{plannerRunsError}</span>
                        </TableCell>
                      </TableRow>
                    )}
                    {!plannerRunsLoading && !plannerRunsError && plannerRunsRows.map((run) => {
                      const planner = PLANNERS_DATA.find((p) => p.planner_id === run.planner_id);
                      const teamRun = TEAM_RUNS_DATA.find((tr) => tr.team_run_id === run.team_run_id);
                      const team = teamRun ? TEAMS_DATA.find((t) => t.team_id === teamRun.team_id) : null;
                      const parent = run.parent_run_context || {};
                      const id = run.planner_run_id || run.run_id;
                      const recordId = run.metric_id || run.id;
                      const plannerName = run.metric_type_name || (planner ? planner.planner_name : '');
                      const teamName =
                        parent && parent.entity_type === 'pipeline' && parent.entity_name
                          ? parent.entity_name
                          : team
                          ? team.team_name
                          : '';
                      const llmModel = (run.run_data && run.run_data.model) || run.llm_model || '';
                      return (
                        <TableRow
                          key={id}
                          style={{ cursor: recordId ? 'pointer' : 'default' }}
                          onClick={() => recordId && navigate(`/observability/details/${recordId}`)}
                        >
                          <TableCell>{id}</TableCell>
                          <TableCell>{plannerName}</TableCell>
                          <TableCell>{teamName}</TableCell>
                          <TableCell>
                            <StatusBadge status={getStatusBadgeStatus(run.status)}>
                              {run.status}
                            </StatusBadge>
                          </TableCell>
                          <TableCell>{formatRelativeTime(run.started_at)}</TableCell>
                          <TableCell>
                            {run.duration_ms != null
                              ? formatDurationMs(run.duration_ms)
                              : <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>n/a</span>}
                          </TableCell>
                          <TableCell>{run.total_tokens}</TableCell>
                          <TableCell>
                            <span
                              style={{
                                display: 'inline-flex',
                                padding: '2px 8px',
                                borderRadius: '999px',
                                border: '1px solid #e2e8f0',
                                fontSize: '0.75rem'
                              }}
                            >
                              {llmModel}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <Button
                                style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDetailsModal(
                                    'Planner Run – ' + id,
                                    [
                                      { label: 'Run ID', value: id },
                                      { label: 'Planner', value: plannerName },
                                      { label: 'Team', value: teamName },
                                      { label: 'Status', value: run.status },
                                      { label: 'Started At', value: formatRelativeTime(run.started_at) },
                                      { label: 'Finished At', value: run.finished_at || 'n/a' },
                                      { label: 'Duration (ms)', value: run.duration_ms != null ? run.duration_ms : 'n/a' },
                                      { label: 'Total Tokens', value: run.total_tokens },
                                      { label: 'LLM Model', value: llmModel }
                                    ],
                                    PLANNER_TOOL_CALLS[run.planner_run_id] || [],
                                    'planner'
                                  );
                                }}
                              >
                                Details
                              </Button>
                              {['success', 'failed'].includes(String(run.status).toLowerCase()) && (
                                <Button
                                  style={{
                                    padding: '0.3rem 0.6rem',
                                    fontSize: '0.8rem',
                                    backgroundColor: 'transparent',
                                    color: '#FF3B30',
                                    border: '1px solid #FF3B30'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    retryPlannerRun(run.planner_run_id);
                                  }}
                                  title="Retry"
                                >
                                  <FontAwesomeIcon icon={faRedo} />
                                </Button>
                              )}
                              {String(run.status).toLowerCase() === 'running' && (
                                <Button
                                  style={{
                                    padding: '0.3rem 0.6rem',
                                    fontSize: '0.8rem',
                                    backgroundColor: 'transparent',
                                    color: '#FF3B30',
                                    border: '1px solid #FF3B30'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    pausePlannerRun(run.planner_run_id);
                                  }}
                                  title="Pause"
                                >
                                  <FontAwesomeIcon icon={faPause} />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </tbody>
                </Table>
                <Pagination
                  currentPage={plannerRunPagination?.currentPage}
                  totalPages={plannerRunPagination?.totalPages}
                  totalCount={plannerRunPagination?.totalItems}
                  setCurrentPage={plannerRunPagination?.setCurrentPage}
                  setTotalPages={plannerRunPagination?.setTotalPages}
                />
              </TabContent>
            </TabContainer>
          </CardContent>
        </Card> */}

          {/* Agents section: Agents & Agent Sessions */}
          {/* <Card style={{ marginTop: '1.5rem', marginBottom: '2rem' }}>
          <CardTitle>Agents</CardTitle>
          <CardContent>
            <TabContainer>
              <TabList>
                <Tab
                  active={agentsTab === 'agents'}
                  onClick={() => setAgentsTab('agents')}
                  style={{ fontWeight: 600 }}
                >
                  Agents
                </Tab>
                <Tab
                  active={agentsTab === 'agent-runs'}
                  onClick={() => setAgentsTab('agent-runs')}
                  style={{ fontWeight: 600 }}
                >
                  Agent Runs
                </Tab>
              </TabList>

              <TabContent active={agentsTab === 'agents'}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.75rem',
                    gap: '0.75rem',
                    flexWrap: 'wrap'
                  }}
                >
                  <SearchInput
                    type="text"
                    placeholder="Search agents..."
                    value={agentsSearch}
                    onChange={(e) => {
                      setAgentsSearch(e.target.value);
                      setAgentsPage(1);
                    }}
                    style={{ maxWidth: '260px' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  
                  </div>
                </div>
                <Table>
                  <thead>
                    <tr>
                      <TableHeader>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                          <Bot size={14} />
                          <span>Agent</span>
                        </span>
                      </TableHeader>
                      <TableHeader>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                          <Workflow size={14} />
                          <span>Planner</span>
                        </span>
                      </TableHeader>
                      <TableHeader>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                          <Network size={14} />
                          <span>Team</span>
                        </span>
                      </TableHeader>
                      <TableHeader>Core Module Mapping</TableHeader>
                      <TableHeader>Shared Agent</TableHeader>
                      <TableHeader>Role</TableHeader>
                      <TableHeader>LLM Model</TableHeader>
                      <TableHeader>Status</TableHeader>
                      <TableHeader>Last Active</TableHeader>
                      <TableHeader>Actions</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {agentsRows.map((agent) => {
                      const planner = PLANNERS_DATA.find((p) => p.planner_id === agent.planner_id);
                      const team = TEAMS_DATA.find((t) => t.team_id === agent.team_id);
                      return (
                        <TableRow key={agent.agent_id}>
                          <TableCell>{agent.agent_name}</TableCell>
                          <TableCell>{planner ? planner.planner_name : ''}</TableCell>
                          <TableCell>{team ? team.team_name : ''}</TableCell>
                          <TableCell>{agent.core_module_mapping}</TableCell>
                          <TableCell>
                            {agent.shared_agent ? (
                              <span
                                style={{
                                  display: 'inline-flex',
                                  padding: '2px 8px',
                                  borderRadius: '999px',
                                  border: '1px solid #e2e8f0',
                                  fontSize: '0.75rem'
                                }}
                              >
                                Yes
                              </span>
                            ) : (
                              ''
                            )}
                          </TableCell>
                          <TableCell>{agent.role}</TableCell>
                          <TableCell>
                            <span
                              style={{
                                display: 'inline-flex',
                                padding: '2px 8px',
                                borderRadius: '999px',
                                border: '1px solid #e2e8f0',
                                fontSize: '0.75rem'
                              }}
                            >
                              {agent.llm_model}
                            </span>
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={getStatusBadgeStatus(agent.effective_status)}>
                              {agent.effective_status}
                            </StatusBadge>
                          </TableCell>
                          <TableCell>{agent.last_active}</TableCell>
                          <TableCell>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <Button
                                style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}
                                onClick={() =>
                                  openDetailsModal('Agent – ' + agent.agent_name, [
                                    { label: 'Agent ID', value: agent.agent_id },
                                    { label: 'Agent', value: agent.agent_name },
                                    { label: 'Planner', value: planner ? planner.planner_name : '' },
                                    { label: 'Team', value: team ? team.team_name : '' },
                                    { label: 'Core Module Mapping', value: agent.core_module_mapping },
                                    { label: 'Shared Agent', value: agent.shared_agent ? 'Yes' : 'No' },
                                    { label: 'Role', value: agent.role },
                                    { label: 'LLM Model', value: agent.llm_model },
                                    { label: 'Status', value: agent.status },
                                    { label: 'Last Active', value: agent.last_active }
                                  ])
                                }
                              >
                                Details
                              </Button>
                              {String(agent.status).toLowerCase() === 'active' && (
                                <Button
                                  style={{
                                    padding: '0.3rem 0.6rem',
                                    fontSize: '0.8rem',
                                    backgroundColor: 'transparent',
                                    color: '#FF3B30',
                                    border: '1px solid #FF3B30'
                                  }}
                                  onClick={() => deactivateAgent(agent.agent_id)}
                                >
                                  Deactivate
                                </Button>
                              )}
                              {String(agent.status).toLowerCase() === 'inactive' && (
                                <Button
                                  style={{
                                    padding: '0.3rem 0.6rem',
                                    fontSize: '0.8rem',
                                    backgroundColor: 'transparent',
                                    color: '#22C55E',
                                    border: '1px solid #22C55E'
                                  }}
                                  onClick={() => activateAgent(agent.agent_id)}
                                >
                                  Activate
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </tbody>
                </Table>
              </TabContent>

              <TabContent active={agentsTab === 'agent-runs'}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.75rem',
                    gap: '0.75rem',
                    flexWrap: 'wrap'
                  }}
                >
                  <SearchInput
                    type="text"
                    placeholder="Search agent runs..."
                    value={agentRunsSearch}
                    onChange={handleAgentRunsSearchChange}
                    style={{ maxWidth: '260px' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              
                  </div>
                </div>
                <Table>
                  <thead>
                    <tr>
                      <TableHeader>Agent Session ID</TableHeader>
                      <TableHeader>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                          <Bot size={14} />
                          <span>Agent</span>
                        </span>
                      </TableHeader>
                      <TableHeader>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                          <Network size={14} />
                          <span>Team</span>
                        </span>
                      </TableHeader>
                      <TableHeader>Status</TableHeader>
                      <TableHeader>Started At</TableHeader>
                      <TableHeader>Duration (ms)</TableHeader>
                      <TableHeader>Total Tokens</TableHeader>
                      <TableHeader>LLM Model</TableHeader>
                      <TableHeader>Actions</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {agentRunsLoading && (
                      <TableRow>
                        <TableCell colSpan={10}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.5rem 0' }}>
                            <InlineSpinner />
                            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Loading agent runs...</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    {agentRunsError && !agentRunsLoading && (
                      <TableRow>
                        <TableCell colSpan={10}>
                          <span style={{ fontSize: '0.85rem', color: '#FF3B30' }}>{agentRunsError}</span>
                        </TableCell>
                      </TableRow>
                    )}
                    {!agentRunsLoading && !agentRunsError && agentRunsRows.map((session) => {
                      const agent = AGENTS_DATA.find((a) => a.agent_id === session.agent_id);
                      const planner = PLANNERS_DATA.find((p) => p.planner_id === session.planner_id);
                      const team = TEAMS_DATA.find((t) => t.team_id === session.team_id);

                      const id = session.agent_session_id || session.run_id;
                      const recordId = session.metric_id || session.id;
                      const parent = session.parent_run_context || {};
                      const agentName = session.metric_type_name || (agent ? agent.agent_name : '');
                      const teamName =
                        parent && parent.entity_type === 'pipeline' && parent.entity_name
                          ? parent.entity_name
                          : team
                          ? team.team_name
                          : '';
                      const llmModel = (session.run_data && session.run_data.model) || session.llm_model || '';
                      return (
                        <TableRow
                          key={id}
                          style={{ cursor: recordId ? 'pointer' : 'default' }}
                          onClick={() => recordId && navigate(`/observability/details/${recordId}`)}
                        >
                          <TableCell>{id}</TableCell>
                          <TableCell>{agentName}</TableCell>
                          <TableCell>{teamName}</TableCell>
                          <TableCell>
                            <StatusBadge status={getStatusBadgeStatus(session.status)}>
                              {session.status}
                            </StatusBadge>
                          </TableCell>
                          <TableCell>{formatRelativeTime(session.started_at)}</TableCell>
                          <TableCell>
                            {session.duration_ms != null
                              ? formatDurationMs(session.duration_ms)
                              : <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>n/a</span>}
                          </TableCell>
                          <TableCell>{session.total_tokens}</TableCell>
                          <TableCell>
                            <span
                              style={{
                                display: 'inline-flex',
                                padding: '2px 8px',
                                borderRadius: '999px',
                                border: '1px solid #e2e8f0',
                                fontSize: '0.75rem'
                              }}
                            >
                              {llmModel}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <Button
                                style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDetailsModal(
                                    'Agent Session – ' + session.agent_session_id,
                                    [
                                      { label: 'Session ID', value: session.agent_session_id },
                                      { label: 'Agent', value: agentName },
                                      { label: 'Planner', value: planner ? planner.planner_name : '' },
                                      { label: 'Team', value: teamName },
                                      { label: 'Status', value: session.status },
                                      { label: 'Started At', value: formatRelativeTime(session.started_at) },
                                      { label: 'Finished At', value: session.finished_at || 'n/a' },
                                      { label: 'Duration', value: session.duration_ms != null ? formatDurationMs(session.duration_ms) : 'n/a' },
                                      { label: 'Total Tokens', value: session.total_tokens },
                                      { label: 'LLM Model', value: llmModel },
                                      { label: 'Tool Call Count', value: session.tool_call_count }
                                    ],
                                    AGENT_TOOL_CALLS[session.agent_session_id] || [],
                                    'agent'
                                  );
                                }}
                              >
                                Details
                              </Button>
                              {['success', 'failed'].includes(String(session.status).toLowerCase()) && (
                                <Button
                                  style={{
                                    padding: '0.3rem 0.6rem',
                                    fontSize: '0.8rem',
                                    backgroundColor: 'transparent',
                                    color: '#FF3B30',
                                    border: '1px solid #FF3B30'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    retryAgentSession(session.agent_session_id);
                                  }}
                                  title="Retry"
                                >
                                  <FontAwesomeIcon icon={faRedo} />
                                </Button>
                              )}
                              {String(session.status).toLowerCase() === 'running' && (
                                <Button
                                  style={{
                                    padding: '0.3rem 0.6rem',
                                    fontSize: '0.8rem',
                                    backgroundColor: 'transparent',
                                    color: '#FF3B30',
                                    border: '1px solid #FF3B30'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    pauseAgentSession(session.agent_session_id);
                                  }}
                                  title="Pause"
                                >
                                  <FontAwesomeIcon icon={faPause} />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </tbody>
                </Table>
                <Pagination
                  currentPage={agentRunPagination?.currentPage}
                  totalPages={agentRunPagination?.totalPages}
                  totalCount={agentRunPagination?.totalItems}
                  setCurrentPage={agentRunPagination?.setCurrentPage}
                  setTotalPages={agentRunPagination?.setTotalPages}
                />
              </TabContent>
            </TabContainer>
          </CardContent>
        </Card> */}
        </div>

        {/* Details modal */}
        <Modal
          isOpen={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          title={detailsTitle}
          size="xlarge"
          hideFooter
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              fontSize: '0.9rem'
            }}
          >
            {detailsFields.map((field) => (
              <div
                key={field.label}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '1.5rem',
                  borderBottom: '1px dashed #e2e8f0',
                  paddingBottom: '0.4rem'
                }}
              >
                <div style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>
                  {field.label}
                </div>
                <div style={{ flex: 1, textAlign: 'right', color: 'var(--text-primary)' }}>
                  {field.value}
                </div>
              </div>
            ))}

            {Array.isArray(detailsToolCalls) && (
              <div style={{ marginTop: '1rem' }}>
                <div
                  style={{
                    fontWeight: 600,
                    marginBottom: '0.5rem',
                    fontSize: '0.95rem',
                    color: 'var(--text-primary)'
                  }}
                >
                  Tool Calls
                </div>

                {detailsToolCalls.length === 0 ? (
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                    No tool calls recorded for this run.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <Table>
                      <thead>
                        <tr>
                          <TableHeader>Step</TableHeader>
                          <TableHeader>Time</TableHeader>
                          {detailsToolCallsType === 'planner' && (
                            <TableHeader>Tool</TableHeader>
                          )}
                          {detailsToolCallsType === 'planner' && (
                            <TableHeader>Agent / Member</TableHeader>
                          )}
                          {detailsToolCallsType === 'agent' && (
                            <TableHeader>Tool</TableHeader>
                          )}
                          <TableHeader>Status</TableHeader>
                          <TableHeader>Task</TableHeader>
                          <TableHeader>Child Run</TableHeader>
                          <TableHeader>Stop After Tool</TableHeader>
                          <TableHeader>Needs Confirm</TableHeader>
                          <TableHeader>Needs Input</TableHeader>
                        </tr>
                      </thead>
                      <tbody>
                        {detailsToolCalls.map((call, index) => {
                          const task = call.tool_args && call.tool_args.task ? String(call.tool_args.task) : '';
                          const taskPreview = task ? `${task.slice(0, 20)}${task.length > 20 ? '…' : ''}` : '—';
                          const statusLabel = call.tool_call_error ? 'Failed' : 'Success';

                          return (
                            <TableRow key={call.tool_call_id || index}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>{formatToolCallTime(call.created_at)}</TableCell>
                              {detailsToolCallsType === 'planner' && (
                                <TableCell>{call.tool_name || '—'}</TableCell>
                              )}
                              {detailsToolCallsType === 'planner' && (
                                <TableCell>
                                  {call.tool_args && call.tool_args.member_id
                                    ? call.tool_args.member_id
                                    : '—'}
                                </TableCell>
                              )}
                              {detailsToolCallsType === 'agent' && (
                                <TableCell>{call.tool_name || '—'}</TableCell>
                              )}
                              <TableCell>{statusLabel}</TableCell>
                              <TableCell>
                                <span title={task}>{taskPreview}</span>
                              </TableCell>
                              <TableCell>{call.child_run_id || '—'}</TableCell>
                              <TableCell>{formatYesNo(call.stop_after_tool_call)}</TableCell>
                              <TableCell>{formatYesNo(call.requires_confirmation)}</TableCell>
                              <TableCell>{formatYesNo(call.requires_user_input)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal>

        {/* Configure Thresholds modal */}
        <ConfigureThresholdModal
          isOpen={thresholdsOpen}
          onClose={() => setThresholdsOpen(false)}
          config={thresholdConfig}
          onSave={setThresholdConfig}
        />
      </motion.div>
    </PageContainer>
  );
};

export default ObservabilityV2;
